<template>
  <div class="scrollable foodleap-actor-detail" ref="scrollable">
    <template v-if="actor && actor.id">
      <div
        class="fixed right-5 top-[90px] z-[80] px-5 py-3 rounded-[4px] bg-[var(--fl-green)] text-white
               text-[13px] font-medium shadow-[0_12px_30px_rgba(51,65,62,0.2)]"
        role="status"
        v-if="toast"
      >{{ toast }}
      </div>

      <!-- ─────────── Top bar ─────────── -->
      <div class="fl-topbar">
        <nav class="fl-topbar__nav" aria-label="Profile sections">
          <button
            v-for="section of navSections"
            :key="section.key"
            :class="{ 'is-active': activeSection === section.key }"
            @click="scrollToSection(section.key)"
          >{{ section.label }}
          </button>
        </nav>
        <div class="fl-topbar__actions">
          <button class="fl-textbtn" @click="goToStandardProfile">Standard profile</button>
          <button class="fl-textbtn" @click="shareProfile">Share ↗</button>
          <!--
            Stays visible while previewing, because it is the only way back: the preview hides
            every other owner control, this one included if it were gated on showOwnerTools.
          -->
          <button
            class="fl-textbtn"
            v-if="canPreviewAsVisitor"
            :class="{ 'is-active': forcedVisitorPreview }"
            :aria-pressed="forcedVisitorPreview ? 'true' : 'false'"
            @click="toggleVisitorPreview"
          >{{ forcedVisitorPreview ? 'Exit visitor view' : 'View as visitor' }}
          </button>
          <button class="fl-textbtn" v-if="canRunOperations" @click="openOperations">Operations ⚙</button>
          <button class="fl-btn fl-btn--dark fl-btn--small" v-if="showOwnerTools" @click="openEditor(0)">
            Complete your profile <span aria-hidden="true">↗</span>
          </button>
          <button
            class="fl-btn fl-btn--dark fl-btn--small"
            v-else-if="showClaimCta"
            @click="onClickClaimActor"
          >{{ $t('actor_claim') }} <span aria-hidden="true">↗</span></button>
        </div>
      </div>

      <!-- ─────────── Activation ─────────── -->
      <section class="fl-activation" ref="top">
        <div class="fl-loop fl-loop--a"></div>
        <div class="fl-loop fl-loop--b"></div>
        <div class="fl-activation__copy">
          <span class="fl-overline">The next Foodleap · Augmented actor profile</span>

          <!-- The design's copy is written to the profile owner, so visitors get it in the third person. -->
          <template v-if="canUpgrade">
            <h1>Your place in accelerating<br>the food transition<br><em>deserves to be seen.</em></h1>
            <p>
              Upgrade this profile to reflect the role {{ actor.name }} plays across the four
              food transitions—and turn that intelligence into relevant partners, opportunities
              and shared progress.
            </p>
          </template>
          <template v-else>
            <h1 class="fl-activation__title--actor">{{ actor.name }}<br><em>in the food transition.</em></h1>
            <p>
              How this organisation contributes across the four food transitions—the roles it
              plays, the technologies it works with, and where it is looking for partners.
            </p>
          </template>
          <div class="fl-activation__actions">
            <button class="fl-btn fl-btn--primary" v-if="showOwnerTools" @click="openEditor(0)">
              Start your profile upgrade <span aria-hidden="true">↗</span>
            </button>
            <button class="fl-btn fl-btn--primary" v-else-if="showClaimCta" @click="onClickClaimActor">
              Claim &amp; enrich this profile <span aria-hidden="true">↗</span>
            </button>
            <button
              class="fl-btn fl-btn--primary"
              v-else-if="hasContacts"
              @click="scrollToSection('contacts')"
            >{{ $t('conversation_start') }} <span aria-hidden="true">↗</span></button>
            <button class="fl-textlink" @click="scrollToSection('story')">Explore the profile ↓</button>
          </div>
        </div>

        <!-- Owners see why the upgrade is worth it; everyone else sees the snapshot. -->
        <aside class="fl-why-card" v-if="canUpgrade">
          <span class="fl-overline">Why upgrade?</span>
          <div class="fl-why-card__reason" v-for="reason of upgradeReasons" :key="reason.title">
            <b>{{ reason.number }}</b>
            <strong>{{ reason.title }}</strong>
            <p>{{ reason.body }}</p>
          </div>
          <small>About 8 minutes · You can save and continue</small>
        </aside>
        <aside class="fl-why-card fl-why-card--glance" v-else aria-label="Organisation snapshot">
          <span class="fl-overline">At a glance</span>
          <div class="fl-why-card__stat" v-for="stat of heroStats" :key="stat.label">
            <strong>{{ stat.value }}</strong><span>{{ stat.label }}</span>
          </div>
          <div class="fl-health" v-if="profileCompleteness !== null">
            <span>Profile completeness</span><b>{{ profileCompleteness }}%</b>
            <i><em :style="{ width: profileCompleteness + '%' }"></em></i>
          </div>
          <small v-if="originLine">{{ originLine }}</small>
        </aside>
      </section>

      <!-- ─────────── 01 The organisation ─────────── -->
      <section class="fl-section fl-profile-intro" ref="story">
        <div class="fl-profile-heading">
          <!-- `logo` may be a bare path, or absent with a logo.dev fallback — actorLogo resolves both. -->
          <div
            class="fl-logo"
            v-if="resolvedLogo"
            :style="{ backgroundImage: `url(${resolvedLogo})` }"
            :aria-label="actor.name"
          ></div>
          <div class="fl-logo" v-else>
            {{ initials }}
            <button v-if="showOwnerTools" aria-label="Add company logo" @click="openEditor(0)">+</button>
          </div>
          <div>
            <span class="fl-verified" v-if="actor.claimed">Verified organisation ✓</span>
            <h2>{{ actor.name }}</h2>
            <p v-if="localizedActivitiesDescription">{{ localizedActivitiesDescription }}</p>
            <div class="fl-location" v-if="originLine">● {{ originLine }}</div>
            <!-- With the location, not between the blocks below it: both are identity metadata, so
                 the whole card header reads as one thing. One size, one shape — CardInfoSocial
                 sized each icon individually, which is why they never lined up. -->
            <div class="tw-flex flex-wrap items-center gap-2 mt-3" v-if="socialLinks.length">
              <a
                class="tw-flex items-center justify-center w-8 h-8 rounded-full border
                       border-[var(--fl-line)] bg-white text-[var(--fl-ink)] no-underline
                       transition-colors hover:border-[var(--fl-green)] hover:text-[var(--fl-green)]"
                v-for="link of socialLinks"
                :key="link.name"
                :href="link.href"
                :target="link.external ? '_blank' : null"
                :rel="link.external ? 'noopener' : null"
                :title="link.title"
                :aria-label="link.title"
              >
                <icon :name="link.icon" size="14"/>
              </a>
            </div>
          </div>
          <button class="fl-editlink" v-if="showOwnerTools" @click="openEditor(0)">Edit organisation ↗</button>
        </div>

        <div class="fl-facts" v-if="factItems.length">
          <div v-for="fact of factItems" :key="fact.label" :class="{ 'is-primary': fact.primary }">
            <span>{{ fact.label }}</span>
            <strong>{{ fact.value }}</strong>
            <em v-if="fact.score !== undefined && fact.score !== null">{{ fact.score }}/100</em>
          </div>
        </div>

        <div class="fl-company-grid">
          <div class="fl-company-story">
            <span class="fl-section-no">01 / The organisation</span>
            <h3>{{ $t('actor_detail_about') }} {{ actor.name }}.</h3>
            <RichBodyContent class="fl-copy" v-if="localizedDescription" :html="localizedDescription"/>
            <p class="fl-copy" v-else>{{ $t('actor_detail_insufficient_info') }}</p>
            <div class="fl-readmore" v-if="descriptionIsTruncatable">
              <button @click="showFullDescription = !showFullDescription">
                {{ showFullDescription ? 'Read less' : 'Read more' }}
              </button>
            </div>
            <div class="fl-glance" v-if="glanceItems.length">
              <div v-for="item of glanceItems" :key="item.label">
                <b>{{ item.value }}</b><span>{{ item.label }}</span>
              </div>
            </div>
          </div>

          <aside class="fl-products-card" v-if="productItems.length || showOwnerTools">
            <div class="fl-card-head">
              <span>Products &amp; services</span>
              <button v-if="showOwnerTools" @click="openEditor(2)">Edit</button>
            </div>
            <a
              v-for="(product, index) of productItems"
              :key="'product' + product.id"
              @click="handleClickActorCard(product)"
            >
              <b>{{ pad(index + 1) }}</b><span>{{ product.name }}</span><i>↗</i>
            </a>
            <small v-if="productItems.length">Links point to the product profiles on Foodleap.</small>
            <small v-else>No products or services on record yet.</small>
          </aside>
        </div>

        <!-- Expertise and the goals share one panel: both answer "what does this organisation
             stand for", and a second sand block for the icons alone read as a repeat of the
             first. -->
        <div class="fl-expertise" v-if="expertiseItems.length || sustainabilityGoalItems.length">
          <div v-if="expertiseItems.length">
            <span class="fl-section-no">{{ expertiseTitle }}</span>
            <h3>Expertise, with a visible basis.</h3>
            <p>
              No unexplained rating. Expertise is derived from confirmed innovation domains,
              activities, technologies and evidence—and remains editable by the actor.
            </p>
          </div>
          <!-- Without expertise the panel would carry a heading about it and show only icons. -->
          <div v-else>
            <span class="fl-section-no">Impact</span>
            <h3>The goals this work serves.</h3>
            <p>
              The UN goals {{ actor.name }} contributes to. Select one to find every organisation
              on Foodleap working towards it.
            </p>
          </div>
          <div class="fl-expertise__tags" v-if="expertiseItems.length">
            <span v-for="item of expertiseItems" :key="item.label">
              <b>{{ item.label }}</b>
              <small v-if="item.note">{{ item.note }}</small>
            </span>
          </div>

          <!-- Rendered here rather than with ActorDetailSDGs: that component's scoped styles are
               built for the legacy banner (right-aligned, fixed max-widths, clipped at 195px), and
               scoped styles cannot be reshaped from this page. Same images, same tooltips. -->
          <div class="fl-sdgs" v-if="sustainabilityGoalItems.length">
            <span class="fl-sdgs__label" v-if="expertiseItems.length">
              Sustainable Development Goals
            </span>
            <div class="fl-sdgs__row">
              <button
                v-for="goal of sustainabilityGoalItems"
                :key="'sdg' + goal.number"
                v-tooltip.top="goal.label"
                :aria-label="goal.label"
                @click="filterBySustainabilityGoal(goal)"
              >
                <img :src="goal.image" :alt="goal.label" loading="lazy">
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- ─────────── 02 Position in the ecosystem ─────────── -->
      <section class="fl-section fl-roles" v-if="ecosystemRoles.length" ref="roles">
        <div class="fl-section-title">
          <div>
            <span class="fl-section-no">02 / Position in the ecosystem</span>
            <h2>One actor.<br>{{ roleCountLabel }}.</h2>
          </div>
          <p>
            Value-chain roles show what the actor does. Foodleap maps organisations beyond a
            single label—revealing where they create value and who they can work with.
          </p>
        </div>
        <div class="fl-role-list">
          <article v-for="(role, index) of ecosystemRoles" :key="role.label + index">
            <b>{{ pad(index + 1) }}</b>
            <div>
              <h3>{{ role.label }}</h3>
              <span>{{ role.tag }} role</span>
            </div>
            <i>↗</i>
          </article>
        </div>
      </section>

      <!-- ─────────── 03 Transition relevance ─────────── -->
      <section class="fl-section fl-transitions" v-if="transitionEntries.length" ref="transitions">
        <div class="fl-section-title">
          <div>
            <span class="fl-section-no">03 / Transition relevance</span>
            <h2>From ambition<br>to evidence.</h2>
          </div>
          <p>
            Scores combine readiness to scale, strategic impact and connectedness. The narrative
            behind every score is as important as the number.
          </p>
        </div>

        <div
          class="fl-transition-tabs"
          role="tablist"
          aria-label="Food transitions"
          v-if="transitionEntries.length > 1"
        >
          <button
            v-for="(entry, index) of transitionEntries"
            :key="entry.key"
            role="tab"
            :aria-selected="activeTransition === index"
            :style="{ '--fl-accent': entry.color }"
            @click="activeTransition = index"
          >
            <b>{{ pad(index + 1) }}</b>
            <span>{{ entry.label }}</span>
            <em v-if="entry.score !== null">{{ entry.score }}</em>
          </button>
        </div>

        <div class="fl-transition-detail" v-if="transition" :style="{ '--fl-accent': transition.color }">
          <div class="fl-score-block" v-if="transition.score !== null">
            <div class="fl-score-ring" :style="{ '--fl-score': (transition.score * 3.6) + 'deg' }">
              <strong>{{ transition.score }}</strong><small>/100</small>
            </div>
            <span v-if="transition.role">{{ transition.role }}</span>
            <button v-if="showOwnerTools" @click="openEditor(3)">Confirm this assessment ↗</button>
          </div>

          <div class="fl-transition-content">
            <span class="fl-overline">{{ transition.label }}</span>
            <h3>{{ transition.contribution || transition.role || 'Transition contributor' }}</h3>
            <div class="fl-domain-tags" v-if="domainNames.length">
              <span v-for="name of domainNames" :key="name">{{ name }}</span>
            </div>
            <div class="fl-narrative" v-if="narrativeCells.length">
              <div v-for="cell of narrativeCells" :key="cell.label">
                <span>{{ cell.label }}</span>
                <p>{{ cell.value }}</p>
              </div>
            </div>
          </div>

          <div class="fl-metrics" v-if="transition.metrics.length">
            <div v-for="metric of transition.metrics" :key="metric.label">
              <span>{{ metric.label }}</span>
              <b>{{ metric.value }}</b>
              <i><em :style="{ width: metric.value + '%' }"></em></i>
            </div>
            <small v-if="transition.source">{{ transition.source }}</small>
          </div>
        </div>

        <!--
          What actually moves the score. Owners only: it is a set of instructions, and only someone
          who can edit the profile can act on them.

          The copy here is deliberately conservative. Several scoring criteria are switched off in
          the current model, and the impact axis is assessed from the actor's public website rather
          than from anything in this form — so promising that a field will move a number is easy to
          get wrong. See the block comment above the component for the field-by-field mapping.
        -->
        <details class="fl-score-help" v-if="showOwnerTools">
          <summary>
            <b>How to improve this score</b>
            <span>What we read, what we cannot read yet, and where to change it</span>
          </summary>

          <p class="fl-score-help__lead">
            <strong>Filling things in makes your score accurate, not automatically higher.</strong>
            What we do not know is left out of the calculation rather than counted as a zero — so a
            blank leaves part of your position undetermined rather than penalised. Once you fill it
            in, the real value counts, and a modest real value can move a score down as well as up.
            What you get is a position that reflects your business instead of a gap.
          </p>

          <ol class="fl-score-help__list">
            <li v-for="lever of scoreLevers" :key="lever.title">
              <div>
                <b>{{ lever.title }}</b>
                <span>{{ lever.body }}</span>
              </div>
              <button v-if="lever.step !== null" @click="openEditor(lever.step)">
                {{ lever.action }} <span aria-hidden="true">↗</span>
              </button>
            </li>
          </ol>

          <div class="fl-score-help__highlight">
            <b>The biggest lever is not in this form.</b>
            <p>
              Your impact score for each transition is assessed from your <em>public</em> website and
              press coverage — not from what you write here. Concrete evidence scores well: named
              customers, volumes, the share of revenue a transition represents, independent coverage.
              General sustainability language does not, unless someone independent backs it up.
            </p>
          </div>

          <p class="fl-score-help__caveat">
            Some things are not scored yet, and nothing you enter today changes them: sourcing and
            supply reliability, regulatory complexity, and how much of a bottleneck you solve. We
            would rather say so than let you fill in a form that goes nowhere.
          </p>
        </details>
      </section>

      <!-- ─────────── 04 Technology footprint ─────────── -->
      <section
        class="fl-section fl-technology"
        v-if="showTechnologySection"
        ref="technology"
      >
        <div class="fl-section-title">
          <div>
            <span class="fl-section-no">04 / Technology footprint &amp; roadmap</span>
            <h2>What you offer.<br>What you adopt.</h2>
          </div>
          <p>
            We separate the technologies an actor works with from the innovation domains it
            operates in—making capabilities and transformation intent visible.
          </p>
        </div>

        <div class="fl-actor-path">
          <span>{{ (actor.category && actor.category.label) || 'Food-system actor' }}</span>
          <i>{{ technologyRows.length + domainRows.length }} mapped capabilities</i>
          <button v-if="showOwnerTools" @click="openEditor(4)">Edit technology</button>
        </div>

        <div class="fl-tech-grid">
          <div class="fl-tech-card fl-tech-card--main" v-if="technologyRows.length">
            <div class="fl-card-head">
              <span>{{ technologyTitle }}</span><em>Capability</em>
            </div>
            <div class="fl-tech-row" v-for="row of technologyRows" :key="'tech' + row.id">
              <b>{{ row.name }}</b>
              <span>{{ row.category || '—' }}</span>
            </div>
          </div>
          <div class="fl-tech-card fl-tech-card--muted" v-if="domainRows.length">
            <div class="fl-card-head">
              <span>{{ domainsTitle }}</span><em>Focus</em>
            </div>
            <div class="fl-tech-row" v-for="row of domainRows" :key="'domain' + row.id">
              <b>{{ row.name }}</b>
              <span>{{ row.category || '—' }}</span>
              <em v-if="row.offers">{{ row.offers }}</em>
            </div>
          </div>
        </div>
      </section>

      <!-- ─────────── 05 Needs, offers & match potential ─────────── -->
      <section
        class="fl-section fl-opportunities"
        v-if="seekingItems.length || offeringItems.length || matchCards.length"
        ref="opportunities"
      >
        <div class="fl-section-title">
          <div>
            <span class="fl-section-no">05 / Needs, offers &amp; match potential</span>
            <h2>Make the next<br>connection count.</h2>
          </div>
          <p>
            Matches become more useful when actors clearly state what they need, what they can
            offer and when they want to act.
          </p>
        </div>

        <div class="fl-needs-grid" v-if="seekingItems.length || offeringItems.length">
          <div class="fl-need-card fl-need-card--needs">
            <span>We are looking for</span>
            <h3 v-for="need of seekingItems" :key="'need' + need">→ {{ need }}</h3>
            <p v-if="!seekingItems.length">Nothing on record yet.</p>
            <button v-if="showOwnerTools" @click="openEditor(6)">Add a need</button>
          </div>
          <div class="fl-need-card fl-need-card--offers">
            <span>We can offer</span>
            <h3 v-for="offer of offeringItems" :key="'offer' + offer">→ {{ offer }}</h3>
            <p v-if="!offeringItems.length">Nothing on record yet.</p>
            <button v-if="showOwnerTools" @click="openEditor(6)">Add an offer</button>
          </div>
        </div>

        <div class="fl-match-row" v-if="matchCards.length">
          <article v-for="(card, index) of matchCards" :key="'match' + index">
            <span>{{ card.kind }}</span>
            <h3>{{ card.title }}</h3>
            <p v-if="card.body">{{ card.body }}</p>
            <p v-else-if="card.meta.length">{{ card.meta.join(' · ') }}</p>
            <a v-if="hasContacts" @click="scrollToSection('contacts')">
              {{ $t('conversation_start') }} ↗
            </a>
          </article>
        </div>
      </section>

      <!-- ─────────── 06 Join, for anyone whose organisation is not on the map yet ─────────── -->
      <section class="fl-section bg-[var(--fl-sand)] text-center" v-if="showJoinCta" ref="contacts">
        <div class="max-w-[620px] mx-auto [&_h2]:text-[38px] [&_h2]:leading-[1.08]
                    [&_h2]:tracking-[-0.035em] [&_h2]:mt-4 [&_h2]:mb-5
                    [&_p]:m-0 [&_p]:text-[15px] [&_p]:leading-[1.6] [&_p]:text-[var(--fl-ink)]">
          <span class="fl-section-no">06 / Join Foodleap</span>
          <h2>Put your organisation on the transition map.</h2>
          <p>
            {{ actor.name }} is mapped across the food transitions. Add your own organisation and
            become findable by the people looking for exactly what you do.
          </p>
          <div class="tw-flex flex-col items-center gap-4 mt-8 [&_.fl-btn]:text-[14px] [&_.fl-btn]:px-7 [&_.fl-btn]:py-4">
            <button class="fl-btn fl-btn--dark" @click="goToRegister">
              {{ isLoggedIn ? 'Add my organisation' : 'Register & add my organisation' }}
              <span aria-hidden="true">↗</span>
            </button>
            <button class="fl-textbtn" v-if="!isLoggedIn" @click.prevent="showSignIn">
              Already a member? Sign in
            </button>
          </div>
        </div>
      </section>

      <!-- ─────────── 06 Contacts ─────────── -->
      <section class="fl-section fl-contacts" v-if="showContactsSection" ref="contacts">
        <div class="fl-section-title">
          <div>
            <span class="fl-section-no">06 / Get in touch</span>
            <h2>Talk to the people<br>behind the profile.</h2>
          </div>
          <p v-if="hasContacts">Reach out directly to the contacts who represent {{ actor.name }} on Foodleap.</p>
        </div>
        <p class="text-[12px] text-[var(--fl-muted)]" v-if="contributors.loading">{{ $t('loading') }}</p>
        <!-- Built here rather than with ActorDetailContributors: that component carries the
             admin affordances (contactable toggle, remove, communities) which have no place on a
             public-facing profile, and its card styling belongs to the standard actor pages. -->
        <div class="grid grid-cols-[repeat(auto-fill,minmax(210px,1fr))] gap-5" v-else-if="hasContacts">
          <!-- Soft cards in the same idiom as the personal home cards — rounded, shadowed, lifting
               on hover — rather than the flat ruled tiles the rest of this page uses. -->
          <article
            class="tw-flex flex-col bg-white rounded-2xl border border-[var(--fl-line)] shadow-sm
                   overflow-hidden transition-all duration-200 hover:shadow-lg hover:-translate-y-1
                   motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            v-for="contact of contributors.list"
            :key="'contact' + contact.user_id"
          >
            <div class="h-16 bg-gradient-to-br from-[var(--fl-mint)] via-[var(--fl-pale)] to-white"></div>

            <div class="tw-flex flex-col flex-1 items-center text-center px-5 pb-5 -mt-10">
              <!-- Overlapping the band gives the card depth. Most contributors have no photo, so
                   the initials fill is the common case rather than the fallback. -->
              <span
                class="w-[72px] h-[72px] rounded-full ring-4 ring-white shadow-sm bg-[var(--fl-mint)]
                       bg-cover bg-center tw-flex items-center justify-center text-[20px]
                       font-semibold text-[var(--fl-ink)]"
                :style="contact.photo_url ? { backgroundImage: `url(${contact.photo_url})` } : null"
                aria-hidden="true"
              >{{ contact.photo_url ? '' : contactInitials(contact) }}</span>

              <b class="block mt-3 text-[15px] font-semibold leading-tight">{{ contact.user_name }}</b>
              <!-- department and function are taxonomy ids on team_users, so both need resolving
                   before they are shown — printing them raw put numbers on the card. -->
              <small
                class="block mt-1 text-[12px] leading-[1.4] text-[var(--fl-muted)]"
                v-if="getDepartmentName(contact)"
              >{{ getDepartmentName(contact) }}</small>
              <small
                class="block mt-0.5 text-[11px] leading-[1.4] text-[var(--fl-muted)]"
                v-if="getExperienceLevelName(contact)"
              >{{ getExperienceLevelName(contact) }}</small>
              <a
                class="inline-block mt-2 px-2.5 py-1 rounded-full bg-[var(--fl-pale)] text-[11px]
                       text-[var(--fl-muted)] no-underline transition-colors
                       hover:bg-[var(--fl-mint)] hover:text-[var(--fl-ink)]"
                v-if="contact.phone && contact.phone_published"
                :href="'tel:' + contact.phone"
              >{{ contact.phone }}</a>

              <!-- The wrapper carries mt-auto, not the button: grid rows stretch cards to equal
                   height, so without it the buttons sit at different heights whenever one card has
                   no department. -->
              <div class="mt-auto pt-4 w-full">
                <button
                  class="w-full border-0 rounded-lg py-2.5 bg-[var(--fl-green)] text-[var(--fl-ink)]
                         font-[inherit] text-[12px] font-semibold cursor-pointer transition-colors
                         duration-200 hover:bg-[var(--fl-ink)] hover:text-white focus-visible:outline-2
                         focus-visible:outline-offset-2 focus-visible:outline-[var(--fl-green)]
                         motion-reduce:transition-none"
                  v-if="contact.user_id !== $store.getters.userId"
                  @click="messageContact(contact)"
                >{{ contact.conversation_id ? 'Open chat' : 'Message' }}
                </button>
                <!-- Seeing your own half-empty card next to complete ones is the moment the gap
                     registers, so that is where the prompt to fill it belongs. -->
                <button
                  class="w-full border border-dashed border-[var(--fl-green)] rounded-lg py-2.5
                         bg-[var(--fl-mint)] text-[var(--fl-ink)] font-[inherit] text-[12px]
                         font-semibold cursor-pointer transition-colors duration-200
                         hover:bg-[var(--fl-green)] hover:border-solid focus-visible:outline-2
                         focus-visible:outline-offset-2 focus-visible:outline-[var(--fl-green)]
                         motion-reduce:transition-none"
                  v-else-if="contactGaps(contact).length"
                  :title="'Missing: ' + contactGaps(contact).join(', ')"
                  @click="editOwnProfile"
                >Add your {{ contactGaps(contact)[0] }}
                </button>
                <!-- Your own completed card keeps the same foot, so no tile sits unfinished. -->
                <span
                  class="block rounded-lg py-2.5 bg-[var(--fl-pale)] text-[12px] text-[var(--fl-muted)]"
                  v-else
                >This is you</span>
              </div>
            </div>
          </article>
        </div>
      </section>

      <!-- ─────────── Closing ─────────── -->
      <section class="fl-closing">
        <!-- Owners are asked to complete the profile; visitors are given a way to act on it. -->
        <template v-if="canUpgrade">
          <h2>Your place in accelerating the food transition deserves to be seen.</h2>
          <p>
            A complete profile makes your contribution visible—and your next opportunity easier
            to find.
          </p>
        </template>
        <template v-else>
          <h2 class="fl-closing__title--actor">Work with {{ actor.name }}.</h2>
          <p>
            Foodleap maps how this organisation contributes to the food transition—so the next
            collaboration is easier to find.
          </p>
        </template>

        <div class="fl-closing__actions">
          <button class="fl-btn fl-btn--primary" v-if="showOwnerTools" @click="openEditor(0)">
            Complete your profile <span aria-hidden="true">↗</span>
          </button>
          <button class="fl-btn fl-btn--primary" v-else-if="showClaimCta" @click="onClickClaimActor">
            Claim &amp; enrich this profile <span aria-hidden="true">↗</span>
          </button>
          <button
            class="fl-btn fl-btn--primary"
            v-else-if="visitorAction"
            @click="scrollToSection(visitorAction.section)"
          >{{ visitorAction.label }} <span aria-hidden="true">↗</span></button>
          <button class="fl-btn fl-btn--ghost" @click="shareProfile">Share profile</button>
        </div>
      </section>

      <!-- ─────────── More profiles ─────────── -->
      <section class="fl-more" v-if="nextActors.length" ref="more">
        <span class="fl-section-no">{{ $t('more_actor_profiles') }}</span>
        <div class="fl-more__grid">
          <simplified-image-card
            v-for="similarActor of nextActors.slice(0, 3)"
            :key="similarActor.id"
            :full-size="true"
            :name="similarActor.name"
            :image="similarActor.featured_image_url || similarActor.website_screenshot || getDefaultFallbackImageUrl()"
            @click="handleClickActorCard(similarActor)"
          />
        </div>
      </section>

    </template>

    <!-- No profile to show: loading, a claim in review, not signed in, or no access. Written in the
         page's own language rather than the generic actor-detail empty state, because for an
         anonymous visitor this screen *is* the product's first impression. -->
    <template v-else>
      <div class="tw-flex items-center justify-center min-h-[62vh] py-[90px] px-[7vw]
                  bg-[var(--fl-sand)] text-center">
        <div
          class="max-w-[560px] [&_h2]:text-[34px] [&_h2]:leading-[1.1] [&_h2]:tracking-[-0.035em] [&_h2]:mt-4 [&_h2]:mb-4 [&_p]:m-0 [&_p]:text-[15px] [&_p]:leading-[1.6] [&_p]:text-[var(--fl-muted)]"
          v-if="loading">
          <span class="fl-overline">Foodleap</span>
          <h2>{{ $t('loading') }}</h2>
        </div>

        <div
          class="max-w-[560px] [&_h2]:text-[34px] [&_h2]:leading-[1.1] [&_h2]:tracking-[-0.035em] [&_h2]:mt-4 [&_h2]:mb-4 [&_p]:m-0 [&_p]:text-[15px] [&_p]:leading-[1.6] [&_p]:text-[var(--fl-muted)]"
          v-else-if="actorClaimInProgress">
          <span class="fl-overline">Claim in review</span>
          <h2>Your claim is on its way.</h2>
          <p>{{ $t('actor_error_claim_processing') }}</p>
          <div class="tw-flex flex-col items-center gap-4 mt-8 [&_.fl-btn]:text-[14px] [&_.fl-btn]:px-7 [&_.fl-btn]:py-4">
            <button class="fl-btn fl-btn--dark" @click="$router.push('/')">
              {{ $t('go_to_homepage') }}
            </button>
          </div>
        </div>

        <div
          class="max-w-[560px] [&_h2]:text-[34px] [&_h2]:leading-[1.1] [&_h2]:tracking-[-0.035em] [&_h2]:mt-4 [&_h2]:mb-4 [&_p]:m-0 [&_p]:text-[15px] [&_p]:leading-[1.6] [&_p]:text-[var(--fl-muted)]"
          v-else-if="!isLoggedIn">
          <span class="fl-overline">Foodleap</span>
          <h2>See how organisations are driving the food transition.</h2>
          <p>{{ $t('actor_error_login_required') }}</p>
          <div class="tw-flex flex-col items-center gap-4 mt-8 [&_.fl-btn]:text-[14px] [&_.fl-btn]:px-7 [&_.fl-btn]:py-4">
            <button class="fl-btn fl-btn--dark" @click="$router.push('/register')">
              {{ $t('action_register') }} &amp; add my organisation <span aria-hidden="true">↗</span>
            </button>
            <button class="fl-textbtn" @click.prevent="showSignIn">Already a member? Sign in</button>
          </div>
        </div>

        <div
          class="max-w-[560px] [&_h2]:text-[34px] [&_h2]:leading-[1.1] [&_h2]:tracking-[-0.035em] [&_h2]:mt-4 [&_h2]:mb-4 [&_p]:m-0 [&_p]:text-[15px] [&_p]:leading-[1.6] [&_p]:text-[var(--fl-muted)]"
          v-else>
          <span class="fl-overline">No access</span>
          <h2>This profile is not open to you.</h2>
          <p>{{ $t('actor_error_permissions') }}</p>
          <div class="tw-flex flex-col items-center gap-4 mt-8 [&_.fl-btn]:text-[14px] [&_.fl-btn]:px-7 [&_.fl-btn]:py-4">
            <button class="fl-btn fl-btn--dark" @click="$router.push('/')">
              {{ $t('go_to_homepage') }}
            </button>
          </div>
        </div>
      </div>
    </template>

    <SimplifiedFooterFoodleapNew/>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'

  import RichBodyContent from '@/components/RichBodyContent/RichBodyContent.vue'
  import SimplifiedFooterFoodleapNew from '@/components/SimplifiedFooter/SimplifiedFooterFoodleapNew.vue'
  import SimplifiedImageCard from '@/components/Connection/SimplifiedImageCard.vue'

  import CompanyMixin from '@/util/CompanyMixin.js'
  import ConfigMixin from '@/util/ConfigMixin.js'
  import TranslationsMixin from '@/util/TranslationsMixin.js'
  import TaxonomyMixin from '@/Domain/Taxonomy/Mixins/TaxonomyMixin.ts'
  import UiMixin from '@/util/UiMixin.js'
  import UserMixin from '@/util/UserMixin'

  import MODAL_IDS from '@/constants/modal-ids.js'
  import { ACTION_TYPES as ACTORS_ACTION_TYPES, MUTATION_TYPES as ACTORS_MUTATION_TYPES } from '@/store/modules/actors.js'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui.js'
  import { MUTATION_TYPES as FILTERS_MUTATION_TYPES } from '@/store/modules/filters.js'
  import { sustainabilityGoals, sustainabilityGoalsNL } from '@/constants/config.js'
  import { ACTION_TYPES as CONVERSATION_ACTION_TYPES } from '@/store/modules/conversations'
  import { Conversations } from '@/api/conversations'
  import { fetchActorTechnologyAdoptions } from '@/api/actor-technology-adoptions.js'
  import { fetchActorTransitions } from '@/api/actor-transitions.js'
  import { fetchContributors } from '@/api/actors.js'
  import { fetchInnovationRadar, fetchInnovationRadarQuarters } from '@/api/innovation-radar'
  import { fetchTransitionPulse, fetchTransitionPulseMonths } from '@/api/transition-pulse'
  import { getConnectionsForActor, nl2br } from '@/util/helpers.js'
  import { trackHeapEvent } from '@/util/analytics.js'

  const DESCRIPTION_LIMIT = 1000
  const AMOUNT_OF_ACTORS_IN_NEXT_ACTORS_SECTION = 3

  // Augmented-profile palette — see the Foodleap design source (actor-profile).
  const ACCENT_ORANGE = '#ef9d47'
  const ACCENT_GREEN = '#78ad46'
  const ACCENT_BLUE = '#7eb5df'
  const ACCENT_CORAL = '#df6e63'

  /**
   * The four Foodleap food transitions, keyed exactly as the transition pulse report stores
   * them. Colours match the transition tabs in the augmented-profile design.
   */
  const TRANSITIONS = {
    protein: { label: 'Protein shift', color: ACCENT_ORANGE },
    circular: { label: 'Circular food system', color: ACCENT_GREEN },
    health: { label: 'Healthy & sustainable food', color: ACCENT_BLUE },
    upcycling: { label: 'Food upcycling', color: ACCENT_CORAL },
  }

  const UPGRADE_REASONS = [
    { number: '01', title: 'Be understood', body: 'Show your role and evidence across four transitions.' },
    { number: '02', title: 'Be discovered', body: 'Make capabilities, products and technologies searchable.' },
    { number: '03', title: 'Be connected', body: 'Match your needs and offers with relevant actors.' },
  ]

  /**
   * What an actor can change, and what it actually affects — the user-facing half of the scoring
   * notes above. `step` is the upgrade-wizard step that edits it, or null when it is not editable
   * from the profile at all.
   *
   * Every claim here is deliberately hedged to what the current model really does. Nothing in this
   * list promises a higher number; the honest promise is that a filled-in field lets a real number
   * replace an undetermined one.
   */
  const SCORE_LEVERS = [
    {
      title: 'Your category',
      body: 'Sets how central you are assumed to be in the ecosystem, which drives the size of your ' +
        'dot on the radar. Pick the one that describes you most accurately — a network or ' +
        'association sits at one end of that scale, an operating company at the other.',
      action: 'Check your category',
      step: 2,
    },
    {
      title: 'Team size',
      body: 'Counts towards how established your business looks. We read the top of the range you ' +
        'give, so keep it current as you grow.',
      action: 'Update headcount',
      step: 2,
    },
    {
      title: 'Your technology, and how far you have taken it',
      body: 'Your most mature technology sets your readiness — one accurate "deployed" or "scaled" ' +
        'entry is enough, and adding less mature ones alongside it does not dilute that. A ' +
        'technology with no status set is skipped entirely, so a mature product left blank counts ' +
        'for nothing at all.',
      action: 'Set adoption status',
      step: 4,
    },
    {
      title: 'The domains you work in',
      body: 'Guarantees a minimum score on climate, waste and health impact. It cannot reach the top ' +
        'of that range on its own — that needs measured figures such as tonnes of CO₂ or the share ' +
        'of waste diverted, and there is no way to submit those yet.',
      action: 'Review your domains',
      step: 6,
    },
    {
      title: 'Your role in each transition',
      body: 'Two things happen here, and neither is the score itself. Your ambition, activities and ' +
        'outcomes are the story shown next to the number, so a reader sees your reasoning rather ' +
        'than a bare figure. And once you add at least one piece of evidence alongside them, your ' +
        'own account of which transitions you belong to takes precedence over our automated reading ' +
        'of your business — without evidence, ours stands. If the score itself looks wrong, it is ' +
        'the public evidence below that changes it, not this form.',
      action: 'Describe your role',
      step: 3,
    },
    {
      title: 'Revenue, funding, customers and partners',
      body: 'These come from our records rather than this form, and they matter: revenue and funding ' +
        'move your readiness, while recorded customers and partners move both your readiness and ' +
        'the size of your dot. If any of it is out of date or missing, tell your Foodleap contact.',
      action: null,
      step: null,
    },
  ]

  /* ═══════════════════════════════════════════════════════════════════════════════════════════
   * WHICH PROFILE CHANGES MOVE AN ACTOR'S RADAR SCORE — AND WHICH DO NOT
   * ═══════════════════════════════════════════════════════════════════════════════════════════
   *
   * This page renders the Innovation Radar v2 scores (readiness / impact / connectedness and the
   * quadrant). Below is the mapping from "what an actor edits here" to "what that actually moves",
   * so nobody writes a tooltip or an onboarding email promising something the scorer ignores.
   *
   * Sources — re-diff against these before writing user copy, they are a snapshot not a spec:
   *   · Explainer:   https://datascouts.atlassian.net/wiki/spaces/EcosystemTech/pages/4898783237
   *   · Field guide: https://datascouts.atlassian.net/wiki/spaces/EcosystemTech/pages/4899405827
   *   · IR v2 is a first-pass POC validated on a 20-actor batch, as of 2026-08-04.
   *
   * ── THE ONE RULE TO GET RIGHT ───────────────────────────────────────────────────────────────
   *
   * Filling something in makes a score ACCURATE. It does not guarantee a HIGHER one, and it can
   * make it lower. Two different mechanics get conflated here, so keep them apart:
   *
   *   · A blank is not punished. A criterion with no usable data is excluded from its group's
   *     average rather than scored zero. That is the part that is safe to promise.
   *   · A filled-in field IS scored. Once a real value enters an average it was previously absent
   *     from, a modest value pulls that average down. One criterion scoring 5 with the rest blank
   *     averages 5; add a real 1 next to it and the group is 3.
   *
   * Concrete ways filling something in lowers a score:
   *   · Setting a blank adoption status to "exploring" scores 1.0 where nothing was counted before.
   *   · Setting a category that carries a 0 orchestration prior (most operating companies) where
   *     the category was previously unknown.
   *   · Publishing more about yourself without evidence: transition alignment caps self-reported
   *     claims at 2, and scores a diversified business LOWER than a pure-play serving the same
   *     transition. A broader public story can therefore reduce it.
   *
   *   Say:     "this makes your position reflect your business instead of a gap"
   *   Not:     "filling this in never lowers your score"   ← false, and a promise we would break
   *   Not:     "blank fields hurt your score"              ← also false; blanks are excluded
   *
   * ── WHAT THE ACTOR CAN CHANGE FROM THE UPGRADE WIZARD ───────────────────────────────────────
   *
   * Step 3 · Category (single-select)
   *   Moves:  connectedness (Orchestration dot size), via a fixed category prior.
   *   Detail: Network/association = 4, innovation support / public authority / investor = 3,
   *           research & education / test facility = 2, most operating-company categories = 0.
   *   Say:    pick the single most accurate one. It is the largest single lever on dot size — but
   *           it is a factual classification question, not something to optimise.
   *
   * Step 3 · Employee range
   *   Moves:  readiness, through two separate criteria on two different curves (team strength, and
   *           an averaged "capital + brains" growth score).
   *   Detail: the scorer reads the range's UPPER bound. Bands step at 5 / 20 / 50 / 200 / 1000.
   *   Say:    keep headcount current; the top of the range is what is read.
   *
   * Step 4 · Transitions — relevance and narrative
   *   Moves:  nothing mechanically, and this is the most common misunderstanding on this page.
   *   Detail: the impact axis comes from `transition_alignment`, which is scored by a live web
   *           search over the actor's PUBLIC website — not from what they tick here. Their claims
   *           are read only as a secondary cross-check shown beside the score.
   *   Say:    make the public website state, with evidence, which transition is core to the
   *           business. Vague sustainability language is explicitly capped at 2 unless a third
   *           party corroborates it. Keeping claims consistent with the public story makes the
   *           cross-check read cleanly; it does not move the number.
   *
   * Step 5 · Technology + adoption status
   *   Moves:  readiness, via technology readiness level (TRL).
   *   Detail: the scorer takes the MAX across all tagged technologies —
   *           exploring 1.0 · piloting 2.5 · deployed 4.0 · scaled 5.0.
   *           ⚠ A BLANK status is ignored, not scored 0. A fully mature product with no status set
   *             scores the same as having no technology at all.
   *           ⚠ Our enum also has `roadmap` and `discontinued`. IR v2's table has neither, so they
   *             fall through as "no status" and contribute nothing.
   *   Say:    one accurately-tagged "deployed" or "scaled" technology already secures a strong TRL.
   *           Setting the status on the flagship product is the whole game here.
   *
   * Step 7 · Domains (offered)
   *   Moves:  impact, but only as a FLOOR on CO₂ / waste / health-nutrition criteria.
   *   Detail: floors range 1.0–2.0 depending on the tag; the highest applies, they do not sum.
   *   Say:    accurate tags stop these three from being unscored. They cannot on their own push
   *           them into the strong range — 3+ needs a quantified figure (tonnes CO₂/year, % waste
   *           diverted, clinical data), and there is currently NO channel to submit that.
   *
   * ── WHAT MOVES THE SCORE BUT IS *NOT* EDITABLE HERE ─────────────────────────────────────────
   *
   *   Revenue (operating income) → readiness. Step function with a cliff: <1M = 1.25,
   *     1–10M = 2.5, ≥10M = 5.0. Going 1M → 9M moves nothing; crossing 10M nearly doubles it.
   *   Total funding → readiness. Bands at 500k / 2M / 10M / 50M.
   *   Relationships (customers, distributors, retail, partners) → readiness AND connectedness.
   *     Counted by keyword on the relationship type; bands at 1 / 2–3 / 4+.
   *
   *   ⚠ Relationships are the single biggest gap: the last validation batch had ZERO relationship
   *     data for every actor tested, so this whole group is unscored across the board. They are
   *     also not writable through the external API (see fetchActorProperties()), so the AI stack
   *     cannot populate them either. Anyone promising actors that "adding partners improves your
   *     score" needs a way for them to add partners first.
   *
   * ── WHAT MOVES NOTHING AT ALL (do not promise these) ────────────────────────────────────────
   *
   *   Structurally removed from every formula pending product decisions — not merely unscored:
   *     supply / feedstock reliability · regulatory approval complexity · bottleneck-solving and
   *     value-chain leverage.
   *   Always unscoreable regardless of input: production scale (the taxonomy cannot tell an
   *     actor's own manufacturing capability from tools it merely uses).
   *   Not actor-actionable: consumer acceptance (a population statistic over the whole ecosystem).
   *   Informational only, never scored: value-chain position, and role in value chain beyond
   *     corroborating category.
   *
   * ── KNOWN MISMATCHES WITH OUR OWN DATA MODEL ────────────────────────────────────────────────
   *
   *   1. Transition names must match IR v2 exactly. Its official four are Protein Shift · Food
   *      Upcycling · Healthy and functional food · Regenerative Agriculture, and
   *      resolveTransition() matches on name — so a transition configured under any other wording
   *      is silently skipped on a claim write, or 422s on the scores endpoint. Transitions are
   *      configured per ecosystem in Datalab → Taxonomies; there is no seeded list to fall back on.
   *   2. Scale. IR v2 emits 0–5; actor_transition_scores validates 0–100. Convert before posting,
   *      or pass --score-scale=5 to ds:migrate-radar-to-transition-scores.
   *   3. The uncertainty band (worst / best / uncertainty) has nowhere to be stored — the table
   *      holds only the four numbers and the quadrant.
   * ═══════════════════════════════════════════════════════════════════════════════════════════ */

  export default defineComponent({
    name: 'FoodleapSimplifiedActorDetail',
    components: {
      RichBodyContent,
      SimplifiedFooterFoodleapNew,
      SimplifiedImageCard,
    },
    // TaxonomyMixin resolves the contributor `department` / `function` ids against the ecosystem
    // taxonomies — both are stored on team_users as ids, not labels.
    mixins: [CompanyMixin, ConfigMixin, TranslationsMixin, TaxonomyMixin, UiMixin, UserMixin],
    data () {
      return {
        actor: {},
        contributors: { list: [], loading: false },
        showFullDescription: false,
        activeSection: 'story',
        activeTransition: 0,
        toast: '',
        toastTimeout: null,
        isInitialLoad: false,
        radarPosition: null,
        pulseMentions: [],
        // Transition claims from actor_transitions. Authoritative when present; the pulse/radar
        // path below stays as a fallback for ecosystems with no claims recorded yet.
        actorTransitions: [],
        upgradeReasons: UPGRADE_REASONS,
        scoreLevers: SCORE_LEVERS,
        actorAdoptions: [],
      }
    },
    computed: {
      company () {
        return this.$store.getters.detailActor
      },
      loading () {
        return this.isInitialLoad && this.$store.state.actors.detail.loading
      },
      isLoggedIn () {
        return this.$store.getters.isLoggedIn
      },
      profile () {
        return this.$store.state.user && this.$store.state.user.profile
      },
      actorClaimInProgress () {
        if (!this.profile || !this.profile.claims) {
          return false
        }

        return this.profile.claims.map(claim => claim.id).includes(this.$route.params.id)
      },
      /**
       * `userCanEdit` is true on every actor for team members and owners, so they would never
       * see the visitor variant of this page. `?preview=visitor` forces it for review.
       */
      forcedVisitorPreview () {
        return this.$route.query.preview === 'visitor'
      },
      /**
       * Only worth offering to someone whose own view differs from a visitor's. Deliberately reads
       * the raw capabilities rather than showOwnerTools/showClaimCta, which the preview switches
       * off — otherwise the toggle would disappear on its first use.
       */
      canPreviewAsVisitor () {
        return this.userCanEdit || this.userCanClaim
      },
      /**
       * The operations panel is DataScouts tooling, not an ecosystem feature — the endpoints answer
       * 403 for anyone else, so this only decides whether to render the controls at all.
       *
       * Hidden in visitor preview too: previewing is for seeing what a visitor sees, and internal
       * tooling is precisely what they do not.
       */
      canRunOperations () {
        return !this.forcedVisitorPreview && this.$store.getters.isUserOfDataScoutsTeam
      },
      /**
       * The actor's SDGs as renderable entries.
       *
       * Stored as strings like "SDG 7: Affordable and clean energy", so the number is parsed back
       * out to find the image. `value` stays the English constant even in Dutch — that is what the
       * `sustainability_goal` facet indexes, so it is what a filter has to be given.
       */
      sustainabilityGoalItems () {
        const isDutch = this.$store.getters.activeLocale === 'nl'
        const labels = isDutch ? sustainabilityGoalsNL : sustainabilityGoals
        const path = isDutch ? '/images/SDG_nl/' : '/images/SDG/'

        return (this.actor.sustainability_goal || [])
          .map(goal => {
            const text = String(goal)

            return Number(text.substring(text.indexOf(' ') + 1, text.indexOf(':')))
          })
          // A goal saved in a format we cannot parse would otherwise ask for /images/SDG/NaN.jpg.
          .filter(number => number >= 1 && number <= sustainabilityGoals.length)
          .sort((a, b) => a - b)
          .map(number => ({
            number,
            value: sustainabilityGoals[number - 1],
            label: labels[number - 1],
            image: `${path}${number}.jpg`,
          }))
      },
      showOwnerTools () {
        return !this.forcedVisitorPreview && this.userCanEdit
      },
      showClaimCta () {
        return !this.forcedVisitorPreview && this.userCanClaim
      },
      // `logoUrl` comes from CompanyMixin (util/actor.js `actorLogo`): it prefixes bare paths and
      // falls back to a logo.dev lookup on the actor's website.
      resolvedLogo () {
        return this.logoUrl(this.actor)
      },
      // The activation hero speaks to the owner; visitors get the snapshot instead.
      canUpgrade () {
        return this.showOwnerTools || this.showClaimCta
      },
      initials () {
        return (this.actor.name || '')
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map(part => part.charAt(0).toUpperCase())
          .join('')
      },
      rawDescription () {
        return this.localizedDisplayProperty(this.actor, 'description') || ''
      },
      descriptionIsTruncatable () {
        return this.rawDescription.length > DESCRIPTION_LIMIT
      },
      localizedDescription () {
        if (!this.descriptionIsTruncatable || this.showFullDescription) {
          return nl2br(this.rawDescription)
        }

        return nl2br(this.rawDescription.slice(0, DESCRIPTION_LIMIT) + '...')
      },
      ecosystemRelationships () {
        return this.$store.getters.fullActorRelationships
      },
      connections () {
        return getConnectionsForActor(this.actor, this.ecosystemRelationships)
          .filter(connection => connection.name && connection.name.length > 0)
      },
      productItems () {
        return this.connections
          .filter(connection => connection.actorType === 'Product')
          .slice(0, 5)
      },
      subsidiaries () {
        return this.actor.subsidiaries || []
      },
      countries () {
        const countries = [this.actor.address && this.actor.address.country]
          .concat(this.subsidiaries.map(subsidiary => subsidiary.address && subsidiary.address.country_code))
          .filter(Boolean)

        return [...new Set(countries)]
      },
      employeeSize () {
        const current = this.actor.current_employees && this.actor.current_employees[0]

        if (!current) {
          return null
        }

        if (current.lower_bound === current.upper_bound) {
          return String(current.lower_bound)
        }

        return current.lower_bound + '–' + current.upper_bound
      },
      profileCompleteness () {
        // `completeness` is only exposed to members/owners, so this stays hidden for visitors.
        const completeness = this.actor.completeness

        if (!Number.isFinite(completeness)) {
          return null
        }

        return Math.round(completeness <= 1 ? completeness * 100 : completeness)
      },
      localizedActivitiesDescription () {
        return this.localizedDisplayProperty(this.actor, 'activities_description')
      },
      foundingYear () {
        if (!this.actor.founding_date) {
          return null
        }

        return String(this.actor.founding_date).slice(0, 4)
      },
      originLine () {
        const place = [
          this.actor.address && this.actor.address.city,
          this.actor.address && this.actor.address.country,
        ].filter(Boolean).join(', ')

        return [place, this.foundingYear ? `Founded ${this.foundingYear}` : null]
          .filter(Boolean)
          .join(' · ')
      },
      heroStats () {
        return [
          { value: this.employeeSize, label: 'employees' },
          { value: this.subsidiaries.length ? this.subsidiaries.length + 1 : null, label: 'locations' },
          { value: this.connections.length || null, label: 'connections' },
        ].filter(stat => !!stat.value)
      },
      /** The fact band under the profile heading; the primary transition is highlighted. */
      factItems () {
        const primary = this.primaryTransition

        return [
          { label: 'Actor type', value: this.actor.category && this.actor.category.label },
          { label: 'Company stage', value: (this.actor.stage && this.actor.stage.label) || this.actor.maturity },
          { label: 'Markets', value: this.countries.join(' · ') },
          {
            label: primary && primary.score !== null ? 'Primary transition · highest score' : 'Primary transition',
            value: primary && primary.label,
            score: primary && primary.score,
            primary: true,
          },
        ].filter(item => !!item.value)
      },
      glanceItems () {
        return [
          { value: this.employeeSize, label: 'employees' },
          { value: this.subsidiaries.length ? this.subsidiaries.length + 1 : null, label: 'locations' },
          { value: this.foundingYear, label: 'founded' },
          { value: this.connections.length || null, label: 'active collaborations' },
        ].filter(item => !!item.value)
      },
      /**
       * An actor's expertise is its industries.
       *
       * Not `expertises`: that taxonomy is user-level — people declare it about themselves via
       * team_user_expertises. The actor_expertises pivot exists but is not what anyone fills in,
       * so reading it here left this section empty on every profile that had industries set.
       */
      expertiseSource () {
        if (this.actor.industries && this.actor.industries.length) {
          return { title: this.getTaxonomyAliasWithDefault('industries'), items: this.actor.industries }
        }

        if (this.actor.business_aspects && this.actor.business_aspects.length) {
          return { title: this.getTaxonomyAliasWithDefault('business_aspects'), items: this.actor.business_aspects }
        }

        return { title: '', items: [] }
      },
      expertiseTitle () {
        return this.expertiseSource.title || 'Domain expertise'
      },
      expertiseItems () {
        const items = this.expertiseSource.items
        const names = items.reduce((byId, item) => ({ ...byId, [item.id]: item.name }), {})

        return items
          .map(item => {
            // Industries nest through `industry_id` rather than the `parent_id` / `parentId` the
            // other taxonomies use, so parentIdOf does not see it.
            const parentId = this.parentIdOf(item) || item.industry_id || null

            return {
              label: item.label || item.name,
              // The basis for the expertise, when the taxonomy carries one.
              note: item.category || item.parent_name || (parentId ? names[parentId] || null : null),
            }
          })
          .filter(item => !!item.label)
          .slice(0, 5)
      },
      ecosystemRoles () {
        const roles = []
        const activities = this.actor.activities || []
        const memberships = this.actor.membership || []

        if (this.actor.category && this.actor.category.label) {
          roles.push({ label: this.actor.category.label, tag: 'Primary' })
        }

        activities.forEach(activity => {
          roles.push({ label: activity.label || activity.name, tag: 'Core' })
        })

        memberships.forEach(membership => {
          roles.push({ label: membership.label || membership.name, tag: 'Supporting' })
        })

        const seen = new Set()

        return roles
          .filter(role => {
            if (!role.label || seen.has(role.label)) {
              return false
            }

            seen.add(role.label)

            return true
          })
          .slice(0, 6)
      },
      roleCountLabel () {
        const counts = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six']
        const count = this.ecosystemRoles.length

        return `${counts[count] || count} essential role${count === 1 ? '' : 's'}`
      },
      /**
       * One entry per food transition the actor is documented in, merging the innovation radar
       * position (readiness / impact / connectedness) with the transition pulse rationale.
       */
      /** Transitions the actor has actually answered, from actor_transitions. */
      answeredTransitions () {
        return this.actorTransitions.filter(row => row.answered)
      },
      transitionEntries () {
        // Recorded claims win over the report-derived view: they are per-transition, durable and
        // carry their own scores, where the pulse/radar path shares one score across all four.
        if (this.answeredTransitions.length) {
          return this.answeredTransitions
            .filter(row => row.relevance !== 'not_relevant')
            .map(row => ({
              key: String(row.transition.id),
              label: row.transition.name,
              color: row.transition.hex || ACCENT_GREEN,
              contribution: row.ambition || row.activities || null,
              relevance: row.relevance,
              narrative: row,
              role: row.score && row.score.quadrant,
              score: (row.score && row.score.score) ?? null,
              metrics: this.metricsFromScore(row.score),
              source: this.provenanceLine(row),
            }))
        }

        const entries = this.pulseMentions.map(mention => ({
          key: mention.key,
          label: (TRANSITIONS[mention.key] && TRANSITIONS[mention.key].label) || mention.title || mention.key,
          color: (TRANSITIONS[mention.key] && TRANSITIONS[mention.key].color) || ACCENT_GREEN,
          contribution: mention.meta || null,
          ...this.radarFacts,
        }))

        if (entries.length || !this.radarPosition) {
          return entries
        }

        // No pulse mention, but the actor is positioned on the innovation radar.
        return [{
          key: 'radar',
          label: 'Food transition',
          color: ACCENT_GREEN,
          contribution: null,
          ...this.radarFacts,
        }]
      },
      primaryTransition () {
        if (!this.transitionEntries.length) {
          return null
        }

        return [...this.transitionEntries]
          .sort((a, b) => (b.score || 0) - (a.score || 0))[0]
      },
      radarFacts () {
        const position = this.radarPosition

        if (!position) {
          return { role: null, score: null, metrics: [], source: null }
        }

        const metrics = [
          { label: 'Readiness to scale', value: this.normalizeScore(position.x) },
          { label: 'Strategic impact', value: this.normalizeScore(position.y) },
          { label: 'Connectedness', value: this.normalizeScore(position.orchestrationScore) },
        ].filter(metric => metric.value !== null)

        const score = metrics.length
          ? Math.round(metrics.reduce((total, metric) => total + metric.value, 0) / metrics.length)
          : null

        return {
          role: position.label || null,
          score,
          metrics,
          source: position.reportTitle ? `Source: ${position.reportTitle}` : null,
        }
      },
      transition () {
        return this.transitionEntries[this.activeTransition] || this.transitionEntries[0] || null
      },
      /** Only the narrative we actually hold — the design's empty cells are left out. */
      narrativeCells () {
        if (!this.transition) {
          return []
        }

        const narrative = this.transition.narrative

        if (narrative) {
          return [
            { label: 'Ambition', value: narrative.ambition },
            { label: 'Activities & practices', value: narrative.activities },
            { label: 'Intended outcomes', value: narrative.intended_outcomes },
            { label: 'Observed outcomes', value: narrative.observed_outcomes },
            { label: 'Evidence', value: this.evidenceLine(narrative) },
          ].filter(cell => !!cell.value)
        }

        return [
          { label: 'Contribution', value: this.transition.contribution },
          { label: 'Quadrant', value: this.transition.role },
          { label: 'Evidence', value: this.transition.source },
        ].filter(cell => !!cell.value)
      },
      /**
       * What the wizard asks about when the actor has no recorded claims yet.
       *
       * Stays on the page rather than in the modal: working it out means reading the transition
       * pulse and the innovation radar, which is this page's job. The modal prefers the claims it is
       * handed and only falls back to this.
       */
      wizardFallbackTransitions () {
        if (this.transitionEntries.length) {
          return this.transitionEntries
        }

        return Object.keys(TRANSITIONS).map(key => ({
          key,
          label: TRANSITIONS[key].label,
          color: TRANSITIONS[key].color,
          contribution: null,
          score: null,
        }))
      },
      technologyTitle () {
        return this.getTaxonomyAliasWithDefault('technology') || 'Technology'
      },
      domainsTitle () {
        return this.getTaxonomyAliasWithDefault('domains') || 'Innovation domains'
      },
      technologyRows () {
        return this.taxonomyRows('technology')
      },
      domainRows () {
        return this.taxonomyRows('domains').map(row => {
          const domain = (this.actor.domains || []).find(item => item.id === row.id)

          return {
            ...row,
            offers: domain && domain.offers && domain.offers.length
              ? domain.offers.join(' · ')
              : null,
          }
        })
      },
      domainNames () {
        return this.domainRows.map(row => row.name).slice(0, 6)
      },
      /** What the actor stated it is looking for in the ecosystem. */
      seekingItems () {
        return (this.actor.motivations || [])
          .map(motivation => motivation.label || motivation.name)
          .filter(Boolean)
          .slice(0, 5)
      },
      /** Domains and aspects the actor sells a product or a service in. */
      offeringItems () {
        return (this.actor.domains || [])
          .concat(this.actor.aspects || [])
          .filter(item => item && item.name && item.offers && item.offers.length)
          .map(item => item.name)
          .slice(0, 5)
      },
      matchCards () {
        const cards = []
        const offerings = (this.actor.domains || []).concat(this.actor.aspects || [])
        const motivations = this.actor.motivations || []

        // Offered: domains and aspects the actor sells a product or a service in.
        offerings
          .filter(item => item && item.name && ((item.offers && item.offers.length) || item.remark))
          .forEach(item => {
            cards.push({
              kind: 'Offering',
              title: item.name,
              body: item.remark || null,
              meta: (item.offers || []).slice(),
            })
          })

        // Seeking: what the actor stated it is looking for in the ecosystem.
        motivations.forEach(motivation => {
          cards.push({
            kind: 'Seeking',
            title: motivation.label || motivation.name,
            body: null,
            meta: [],
          })
        })

        return cards.filter(card => !!card.title).slice(0, 3)
      },
      hasContacts () {
        return this.contributors.list && this.contributors.list.length > 0
      },
      // Website / email / phone are the actor's own channels, independent of contributors.
      hasContactDetails () {
        return !!(this.actor.url || this.actor.email || this.actor.phone ||
          (this.actor.other_urls && this.actor.other_urls.length))
      },
      /**
       * The join pitch replaces the contacts section for anyone whose own organisation is not on
       * the map — anonymous visitors, and signed-in users who have not claimed an actor.
       *
       * Reaching the contacts is the reward for being part of the ecosystem, so it is not what a
       * non-member is shown; and a member who has never put their own organisation up is exactly
       * who this asks.
       */
      showJoinCta () {
        return !this.isLoggedIn || this.nonProductClaims.length === 0
      },
      /**
       * Every way to reach the actor, as one uniform list.
       *
       * Built here rather than reusing CardInfoSocial because that component sizes each icon
       * individually (18px for LinkedIn, 14px for Twitter) with per-icon margin nudges, so the row
       * never lines up. One size, one shape, one hover state — and the order is deliberate:
       * the channels a visitor is most likely to use come first.
       */
      socialLinks () {
        const actor = this.actor
        const links = [
          { name: 'website', icon: 'world', href: actor.url, title: 'Website' },
          { name: 'email', icon: 'email', href: actor.email ? `mailto:${actor.email}` : null, title: actor.email, external: false },
          { name: 'phone', icon: 'phone', href: actor.phone ? `tel:${actor.phone}` : null, title: actor.phone, external: false },
          { name: 'linkedin', icon: 'linkedin-logo', href: actor.linkedin, title: 'LinkedIn' },
          { name: 'twitter', icon: 'twitter-logo', href: actor.twitter, title: 'X / Twitter' },
          { name: 'facebook', icon: 'facebook-logo', href: actor.facebook, title: 'Facebook' },
          { name: 'instagram', icon: 'instagram-logo', href: actor.instagram, title: 'Instagram' },
          { name: 'youtube', icon: 'youtube-play', href: actor.youtube, title: 'YouTube' },
          { name: 'vimeo', icon: 'vimeo-logo', href: actor.vimeo, title: 'Vimeo' },
          { name: 'crunchbase', icon: 'crunchbase-logo-letters', href: actor.crunchbase, title: 'Crunchbase' },
          { name: 'wikipedia', icon: 'wikipedia-logo', href: actor.wikipedia, title: 'Wikipedia' },
          { name: 'rss', icon: 'rss', href: actor.rss_feed, title: 'RSS' },
        ]

        return links
          // Social fields can arrive as an array of handles; the first is the canonical one.
          .map(link => ({
            ...link,
            href: Array.isArray(link.href) ? link.href[0] : link.href,
            external: link.external !== false,
          }))
          .filter(link => !!link.href)
      },
      /**
       * Section 06 is about *people* — named humans you can message.
       *
       * `hasContactDetails` used to keep it open too, from when the section also carried the
       * website/email/phone row. That row now lives with the identity block in section 01, so
       * including it here rendered a heading with nothing under it.
       */
      showContactsSection () {
        if (this.showJoinCta) {
          return false
        }

        return this.hasContacts || (this.isLoggedIn && this.contributors.loading)
      },
      /**
       * "What you offer. What you adopt." — signed-in users only.
       *
       * The technology an actor supplies and the technology it runs internally is capability
       * intelligence, so it is not put in front of anonymous visitors. One computed rather than the
       * condition inline, because the section and its entry in the page nav have to agree: a nav
       * link to a section that was not rendered scrolls to nothing.
       */
      showTechnologySection () {
        return this.isLoggedIn && (this.technologyRows.length > 0 || this.domainRows.length > 0)
      },
      navSections () {
        return [
          { key: 'story', label: 'Profile', visible: true },
          { key: 'roles', label: 'Roles', visible: this.ecosystemRoles.length > 0 },
          { key: 'transitions', label: 'Transitions', visible: this.transitionEntries.length > 0 },
          { key: 'technology', label: 'Technology', visible: this.showTechnologySection },
          {
            key: 'opportunities',
            label: 'Opportunities',
            visible: this.seekingItems.length > 0 || this.offeringItems.length > 0 || this.matchCards.length > 0,
          },
          // Both sections use ref="contacts", so one nav entry covers whichever is rendered.
          {
            key: 'contacts',
            label: this.showJoinCta ? 'Join' : 'Contact',
            visible: this.showJoinCta || this.showContactsSection,
          },
        ].filter(section => section.visible)
      },
      visitorAction () {
        if (this.hasContacts) {
          return { label: this.$t('conversation_start'), section: 'contacts' }
        }

        if (this.hasContactDetails) {
          return { label: 'Find contact details', section: 'contacts' }
        }

        if (this.nextActors.length) {
          return { label: 'Browse similar actors', section: 'more' }
        }

        return null
      },
      nextActors () {
        const actorsList = this.$store.state.actors.listData.data
        const index = actorsList.findIndex(actor => actor.id === this.actor.id)

        if (index === -1) {
          return []
        }

        if (index === actorsList.length - 1) {
          return actorsList.slice(0, AMOUNT_OF_ACTORS_IN_NEXT_ACTORS_SECTION)
        }

        return actorsList.slice(index + 1, index + 1 + AMOUNT_OF_ACTORS_IN_NEXT_ACTORS_SECTION)
      },
      nonProductClaims () {
        if (!this.profile || !this.profile.claims) {
          return []
        }

        return this.profile.claims.filter(claim => claim.actor_type !== 'Product')
      },
    },
    methods: {
      /** Two-digit index for the numbered lists (products, roles, transitions). */
      pad (value) {
        return String(value).padStart(2, '0')
      },
      /**
       * Guess which scale a report value is on and bring it to 0–100.
       *
       * The boundary is `< 1`, not `<= 1`: reports carry orchestration scores on a 1–5 scale, and
       * treating an exact 1 as the fractional scale made it render as 100 while 1.5 rendered as 30
       * — the least connected actors showed as the most connected. Six actors in the current
       * protein radar sit at exactly 1.
       *
       * Guessing at all is the underlying problem; scores read from actor_transition_scores are
       * already 0–100 and skip this path entirely.
       */
      normalizeScore (value) {
        const numeric = Number(value)

        if (!Number.isFinite(numeric) || numeric <= 0) {
          return null
        }

        if (numeric < 1) {
          return Math.round(numeric * 100)
        }

        if (numeric <= 5) {
          return Math.round(numeric * 20)
        }

        return Math.round(Math.min(100, numeric))
      },
      /**
       * Hierarchical taxonomies come back as a flat list containing both parents and
       * children. Prefer the leaves and use their parent as the category label.
       *
       * The parent key is not consistent across taxonomies: `technology` and
       * `product_features_*` use `parent_id`, while `domains` and `aspects` are
       * transformed to `parentId` (see OutgoingActorTransformer::transformDomains).
       */
      parentIdOf (item) {
        return item.parent_id || item.parentId || null
      },
      taxonomyRows (taxonomy) {
        const items = this.actor[taxonomy] || []
        const names = items.reduce((byId, item) => ({ ...byId, [item.id]: item.name }), {})
        const children = items.filter(item => !!this.parentIdOf(item))
        const rows = children.length ? children : items

        return rows
          .filter(item => !!item.name)
          .map(item => {
            const parentId = this.parentIdOf(item)

            return {
              id: item.id,
              name: item.name,
              category: parentId ? names[parentId] || null : null,
            }
          })
          .slice(0, 6)
      },
      metricsFromScore (score) {
        if (!score) {
          return []
        }

        return [
          { label: 'Readiness to scale', value: score.readiness_score },
          { label: 'Strategic impact', value: score.impact_score },
          { label: 'Connectedness', value: score.connectedness_score },
        ].filter(metric => Number.isFinite(metric.value))
      },
      /** Who said this and when — the design's "Actor input · reviewed Jul 2026" line. */
      provenanceLine (row) {
        const who = row.source === 'actor_input'
          ? 'Stated by the organisation'
          : (row.source === 'report' ? 'From a published report' : 'Suggested by classification')
        const when = row.reviewed_at ? `reviewed ${row.reviewed_at}` : null

        return [who, when].filter(Boolean).join(' · ')
      },
      evidenceLine (row) {
        if (!row.evidence || !row.evidence.length) {
          return null
        }

        return row.evidence.map(item => item.label).join(' · ')
      },
      scrollToSection (key) {
        const target = this.$refs[key]

        if (!target) {
          return
        }

        this.activeSection = key
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      },
      // Highlights the section currently under the sticky nav.
      updateActiveSection () {
        const container = this.$refs.scrollable

        if (!container) {
          return
        }

        const offset = container.getBoundingClientRect().top + 120
        const current = this.navSections
          .filter(section => {
            const element = this.$refs[section.key]

            return element && element.getBoundingClientRect().top <= offset
          })
          .pop()

        if (current) {
          this.activeSection = current.key
        }
      },
      shareProfile () {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(window.location.href)
          this.showToast('Profile link copied')
        }

        trackHeapEvent('foodleapActorDetail.shareProfile')
      },
      showToast (message) {
        this.toast = message

        if (this.toastTimeout) {
          clearTimeout(this.toastTimeout)
        }

        this.toastTimeout = setTimeout(() => {
          this.toast = ''
        }, 2400)
      },
      /* ── profile upgrade wizard ──────────────────────────────────────── */

      /**
       * Hand the wizard everything it needs and show it.
       *
       * It lives in ModalsContainer, so context travels through the store and results come back over
       * $bus — see the listeners in mounted().
       */
      openEditor (step = 0) {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          actor: this.actor,
          transitions: this.actorTransitions,
          adoptions: this.actorAdoptions,
          products: this.productItems,
          fallbackTransitions: this.wizardFallbackTransitions,
          step,
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.PROFILE_UPGRADE)
      },
      applyUpgradedTransitions (transitions) {
        this.actorTransitions = transitions || []
      },
      applyUpgradedAdoptions (adoptions) {
        this.actorAdoptions = adoptions || []
      },
      /**
       * The join pitch. A signed-in user without a claimed actor is already registered, so they get
       * the add-actor modal (registered globally in ModalsContainer) rather than being sent through
       * registration again.
       */
      goToRegister () {
        trackHeapEvent('foodleapActorDetail.joinCta', { loggedIn: this.isLoggedIn })

        if (this.isLoggedIn) {
          this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_ACTOR)

          return
        }

        this.$router.push('/register')
      },
      openEditSidePanel (actorId) {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
          component: 'actor-edit',
          metaData: { id: actorId },
        })
      },
      onClickClaimActor () {
        trackHeapEvent('foodleapActorDetail.showClaimModal')
        this.$store.commit(ACTORS_MUTATION_TYPES.SET_ACTOR_FOR_PREVIEW, this.company)
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CLAIM)
      },
      showSignIn () {
        if (this.$store.getters.isPrivate) {
          this.$router.push('/login')
        } else {
          this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.LOGIN)
        }
      },
      handleClickActorCard (actor) {
        trackHeapEvent('foodleapActorDetail.clickSimilarActor')
        this.$router.push(`/actors/${actor.id}/aap`)
      },
      goToStandardProfile () {
        this.$router.push(`/actors/${this.actor.id}`)
      },
      /**
       * Flip `?preview=visitor` on the current route.
       *
       * A query-only navigation: nothing watches anything but `$route.params.id`, so the page does
       * not refetch or remount and the reader keeps their scroll position. Pushed rather than
       * replaced so the browser's back button leaves the preview, and other query params are
       * carried over rather than dropped.
       */
      toggleVisitorPreview () {
        const query = { ...this.$route.query }

        if (this.forcedVisitorPreview) {
          delete query.preview
        } else {
          query.preview = 'visitor'
        }

        this.$router.push({ path: this.$route.path, query })

        trackHeapEvent('foodleapActorDetail.toggleVisitorPreview', {
          previewing: !this.forcedVisitorPreview,
        })
      },
      /** Initials, for a contact with no photo — same treatment as the actor avatar. */
      contactInitials (contact) {
        return (contact.user_name || '')
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map(part => part.charAt(0).toUpperCase())
          .join('')
      },
      /**
       * Open the existing conversation, or create one and go to it.
       *
       * Mirrors ActorDetailContributors: the conversation list has to be refreshed before the
       * route change, or the new thread is missing from the sidebar it lands in.
       */
      async messageContact (contact) {
        trackHeapEvent('foodleapActorDetail.messageContact')

        if (contact.conversation_id) {
          this.$router.push(`/profile/conversations/${contact.conversation_id}`)

          return
        }

        try {
          const conversation = await Conversations.post({ recipient_id: contact.user_id })

          await this.$store.dispatch(CONVERSATION_ACTION_TYPES.FETCH_CONVERSATIONS_LIST)
          this.$router.push(`/profile/conversations/${conversation.id}`)
        } catch (errors) {
          this.showToast('Could not start the conversation')
          console.error(errors)
        }
      },
      /**
       * What is missing from your own contributor card, most-visible gap first, so the CTA can
       * name one thing rather than say "complete your profile".
       */
      contactGaps (contact) {
        const gaps = []

        if (!contact.photo_url) {
          gaps.push('photo')
        }

        if (!this.getDepartmentName(contact)) {
          gaps.push('department')
        }

        if (!this.getExperienceLevelName(contact)) {
          gaps.push('role')
        }

        if (!contact.phone || !contact.phone_published) {
          gaps.push('phone number')
        }

        return gaps
      },
      editOwnProfile () {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.EDIT_USER_PROFILE)
      },
      /** Same jump the SDG icons make everywhere else: the directory, filtered to that goal. */
      filterBySustainabilityGoal (goal) {
        this.$store.commit(FILTERS_MUTATION_TYPES.CLEAR)
        this.$store.commit(FILTERS_MUTATION_TYPES.SET_BY_FACET, {
          facet: 'sustainability_goal',
          values: [goal.value],
        })
        this.$router.push('/actors-simplified')
      },
      fetchContributors () {
        this.contributors = { list: [], loading: true }

        fetchContributors(this.$route.params.id, { includeOwners: true })
          .then(list => {
            const contacts = Object.keys(list)
              .map(key => list[key])
              .filter(contact => {
                if (this.hasClaimedActor(this.actor.id)) {
                  return true
                }

                return contact.allow_conversation && contact.user_id !== this.$store.getters.userId
              })

            this.contributors = { list: contacts, loading: false }
          })
          .catch(() => {
            this.contributors = { list: [], loading: false }
          })
      },
      /**
       * Transition relevance comes from the ecosystem-level reports (there is no per-actor
       * endpoint), which are only reachable for authenticated users. Failures are silent:
       * the section simply stays hidden.
       */
      async fetchTransitionRelevance () {
        if (!this.isLoggedIn) {
          return
        }

        await Promise.all([
          this.fetchActorTransitionClaims(),
          this.fetchActorAdoptions(),
          this.fetchRadarPosition(),
          this.fetchPulseMentions(),
        ])
      },
      /**
       * Recorded claims. Failures are silent for the same reason the report calls are: the
       * section falls back to the pulse/radar view rather than breaking the page.
       */
      async fetchActorAdoptions () {
        try {
          const response = await fetchActorTechnologyAdoptions(this.$route.params.id)

          this.actorAdoptions = (response && response.adoptions) || []
        } catch (e) {
          this.actorAdoptions = []
        }
      },
      async fetchActorTransitionClaims () {
        try {
          const response = await fetchActorTransitions(this.$route.params.id)

          this.actorTransitions = (response && response.transitions) || []
        } catch (e) {
          this.actorTransitions = []
        }
      },
      /**
       * The staff operations panel. Lives in a modal rather than on the page: it is tooling, not
       * profile content, and the profile is what everything else here is for.
       */
      openOperations () {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, { actor: this.actor })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.AAP_OPERATIONS)
      },
      async fetchRadarPosition () {
        try {
          const reports = await fetchInnovationRadarQuarters()

          if (!Array.isArray(reports) || !reports.length) {
            return
          }

          const latest = [...reports].sort((a, b) => String(b.date).localeCompare(String(a.date)))[0]
          const report = await fetchInnovationRadar(latest.id)
          const match = (report.actors || []).find(actor => actor.id === this.actor.id)

          if (!match) {
            return
          }

          this.radarPosition = { ...match, reportTitle: report.title || latest.title }
        } catch (e) {
          // Not entitled to the report, or no report published yet.
        }
      },
      async fetchPulseMentions () {
        try {
          const reports = await fetchTransitionPulseMonths()

          if (!Array.isArray(reports) || !reports.length) {
            return
          }

          const latest = [...reports].sort((a, b) => (b.year - a.year) || (b.month - a.month))[0]
          const report = await fetchTransitionPulse(latest.id)
          const categories = (report.report_content && report.report_content.categories) || {}

          this.pulseMentions = Object.keys(categories).reduce((mentions, key) => {
            const category = categories[key]
            const mention = ((category && category.actors) || [])
              .find(actor => actor.elastic_id === this.actor.id)

            if (mention) {
              mentions.push({ key, title: category.title, meta: mention.meta })
            }

            return mentions
          }, [])
        } catch (e) {
          // Not entitled to the report, or no report published yet.
        }
      },
      fetch (force) {
        this.isInitialLoad = !force
        this.radarPosition = null
        this.pulseMentions = []
        this.actorTransitions = []
        this.actorAdoptions = []
        this.activeTransition = 0
        this.showFullDescription = false

        const fetchActorPromise = this.$store.dispatch(
          ACTORS_ACTION_TYPES.FETCH_ACTOR_DETAIL,
          force === true || this.$route.params.id,
        )

        if (!fetchActorPromise) {
          this.fetchContributors()
          this.fetchTransitionRelevance()

          return
        }

        fetchActorPromise
          .then(() => {
            if (!this.nextActors.length) {
              this.$store.dispatch(ACTORS_ACTION_TYPES.FETCH_ACTORS_LIST_NOW, this.$store.getters.listFilterObject)
            }
          })
          .finally(() => {
            if (this.isInitialLoad) {
              trackHeapEvent('foodleapActorDetail.load', {
                id: this.actor.id,
                name: this.actor.name,
              })
            }

            this.isInitialLoad = false
            this.fetchContributors()
            this.fetchTransitionRelevance()
          })
      },
    },
    created () {
      // The detail actor may already be in the store (i.e. coming from a list page),
      // in which case the `company` watcher never fires.
      this.actor = this.company || {}

      this.$store.commit(UI_MUTATION_TYPES.HIDE_SIDE_PANEL)
      this.fetch()
    },
    mounted () {
      this.$bus.on('actorUpdate', (id) => {
        if (id === this.actor.id) {
          this.fetch(true)
        }
      })

      this.$bus.on('updateContributors', this.fetchContributors)
      // The profile modal writes to the user, not the actor, so the contributor list has to be
      // refetched for the filled-in card to appear.
      this.$bus.on('updateUserProfile', this.fetchContributors)

      // The upgrade wizard is rendered by ModalsContainer, so it is not a child of this page and
      // cannot emit to it. It reports what it wrote over the bus instead.
      this.$bus.on('foodleapProfileUpgrade:transitions', this.applyUpgradedTransitions)
      this.$bus.on('foodleapProfileUpgrade:adoptions', this.applyUpgradedAdoptions)
      this.$bus.on('foodleapProfileUpgrade:toast', this.showToast)

      // The operations modal is rendered by ModalsContainer too, so it reports over the bus. A
      // score migration writes claims and scores, which is what the transitions sections render.
      this.$bus.on('aapOperations:migrated', this.fetchActorTransitionClaims)

      if (this.$refs.scrollable) {
        this.$refs.scrollable.addEventListener('scroll', this.updateActiveSection, { passive: true })
      }
    },
    beforeUnmount () {
      this.$store.commit(UI_MUTATION_TYPES.HIDE_SIDE_PANEL)
      this.$bus.off('actorUpdate')
      this.$bus.off('updateContributors')
      // Scoped to the handler: `off(type)` alone clears every listener for the event, and this one
      // is emitted from several modals that other pages may come to listen for.
      this.$bus.off('updateUserProfile', this.fetchContributors)
      this.$bus.off('foodleapProfileUpgrade:transitions')
      this.$bus.off('foodleapProfileUpgrade:adoptions')
      this.$bus.off('foodleapProfileUpgrade:toast')
      this.$bus.off('aapOperations:migrated')

      if (this.$refs.scrollable) {
        this.$refs.scrollable.removeEventListener('scroll', this.updateActiveSection)
      }

      if (this.toastTimeout) {
        clearTimeout(this.toastTimeout)
      }
    },
    watch: {
      company (value) {
        this.actor = value || {}
      },
      '$route.params.id' (to, from) {
        if (to === from) {
          return
        }

        this.fetch()

        if (this.$refs.scrollable) {
          this.$refs.scrollable.scrollTop = 0
        }
      },
    },
  })
</script>

<style lang="scss" scoped>
  .foodleap-actor-detail {
    // Augmented-profile palette — mirrors the design source (actor-profile/app/globals.css).
    --fl-ink: #33413e;
    --fl-green: #5cc61e;
    --fl-mint: #eaf6e6;
    --fl-pale: #f4f8f3;
    --fl-cream: #fcfcfa;
    --fl-sand: #f7f2e8;
    --fl-blue: #70c1ff;
    --fl-coral: #ef374e;
    --fl-line: #dfe8df;
    --fl-muted: #6f7d78;

    background: var(--fl-cream);
    color: var(--fl-ink);
    font-family: 'Fira Sans', Arial, sans-serif;

    ::selection {
      background: var(--fl-green);
      color: #fff;
    }
  }

  /* --- shared type ----------------------------------------------------- */

  .fl-overline,
  .fl-section-no {
    display: block;
    text-transform: uppercase;
    letter-spacing: .12em;
    font-size: 10px;
    font-weight: 500;
    color: var(--fl-muted);
  }

  .fl-btn {
    border: 0;
    border-radius: 4px;
    padding: 15px 21px;
    font-family: inherit;
    font-weight: 500;
    font-size: 13px;
    cursor: pointer;

    span {
      margin-left: 12px;
    }

    transition: background-color .2s, color .2s;

    /* The two fills are inversions of each other, so the tiers stay distinguishable when both
       appear in one block. Ink on green rather than white: white on #5cc61e is ~2.2:1 and fails
       contrast, ink is ~5.5:1 and passes. */
    &--primary {
      background: var(--fl-green);
      color: var(--fl-ink);

      &:hover {
        background: var(--fl-ink);
        color: #fff;
      }
    }

    &--dark {
      background: var(--fl-ink);
      color: #fff;

      &:hover {
        background: var(--fl-green);
        color: var(--fl-ink);
      }
    }

    &--ghost {
      background: transparent;
      color: var(--fl-ink);
      border: 1px solid var(--fl-line);

      &:hover {
        border-color: var(--fl-ink);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    &--small {
      padding: 11px 16px;
    }

    &:disabled {
      opacity: .5;
      cursor: default;
    }
  }

  .fl-textlink,
  .fl-textbtn {
    border: 0;
    background: none;
    padding: 0;
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    color: inherit;
    cursor: pointer;

    &:hover,
    &.is-active {
      color: var(--fl-green);
    }

    // Previewing is a state you can forget you are in, so it stays marked while it is on.
    &.is-active {
      text-decoration: underline;
      text-underline-offset: 3px;
    }
  }

  /* --- top bar --------------------------------------------------------- */

  .fl-topbar {
    min-height: 76px;
    padding: 12px 5vw;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    background: #fff;
    position: sticky;
    top: 0;
    z-index: 30;
    border-bottom: 1px solid var(--fl-line);
  }

  .fl-topbar__nav {
    display: flex;
    gap: 28px;
    font-size: 12px;
    overflow-x: auto;

    button {
      border: 0;
      background: none;
      padding: 4px 0;
      font-family: inherit;
      font-size: 12px;
      color: var(--fl-muted);
      white-space: nowrap;
      cursor: pointer;

      &:hover,
      &.is-active {
        color: var(--fl-green);
      }
    }
  }

  .fl-topbar__actions {
    display: flex;
    align-items: center;
    gap: 18px;
  }

  /* --- activation ------------------------------------------------------ */

  .fl-activation {
    min-height: 680px;
    padding: 82px 7vw;
    display: grid;
    grid-template-columns: 1.35fr .65fr;
    gap: 7vw;
    align-items: center;
    background: var(--fl-mint);
    position: relative;
    overflow: hidden;

    h1 {
      font-size: clamp(44px, 5.8vw, 78px);
      line-height: .98;
      letter-spacing: -.045em;
      margin: 26px 0;

      em {
        font-weight: 300;
        color: #648d3f;
        font-style: normal;
      }
    }

    // An actor name is arbitrary length, unlike the design's hand-set headline.
    h1.fl-activation__title--actor {
      font-size: clamp(36px, 4.6vw, 62px);
      overflow-wrap: anywhere;
    }
  }

  .fl-loop {
    position: absolute;
    border: 55px solid rgba(255, 255, 255, .36);
    border-radius: 50%;
    transform: rotate(-24deg);

    &--a {
      width: 520px;
      height: 250px;
      right: -180px;
      top: -90px;
    }

    &--b {
      width: 420px;
      height: 200px;
      left: -240px;
      bottom: -100px;
      border-color: rgba(126, 181, 223, .18);
    }
  }

  .fl-activation__copy {
    position: relative;

    > p {
      font-size: 17px;
      line-height: 1.7;
      max-width: 760px;
      margin: 0;
    }
  }

  .fl-activation__actions {
    display: flex;
    align-items: center;
    gap: 24px;
    margin-top: 35px;
    flex-wrap: wrap;
  }

  .fl-why-card {
    position: relative;
    background: #fff;
    padding: 31px;
    border-radius: 8px;
    box-shadow: 0 24px 60px rgba(57, 79, 70, .12);

    strong {
      font-size: 16px;
    }

    > small {
      display: block;
      margin-top: 18px;
      color: var(--fl-muted);
      font-size: 11px;
    }
  }

  .fl-why-card__reason {
    display: grid;
    grid-template-columns: 28px 1fr;
    gap: 5px 10px;
    padding: 19px 0;
    border-bottom: 1px solid var(--fl-line);

    > b {
      color: var(--fl-green);
      font-size: 11px;
    }

    p {
      grid-column: 2;
      margin: 0;
      color: var(--fl-muted);
      font-size: 12px;
      line-height: 1.5;
    }
  }

  .fl-why-card__stat {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 17px 0;
    border-bottom: 1px solid var(--fl-line);

    strong {
      font-size: 26px;
      letter-spacing: -.04em;
    }

    span {
      font-size: 12px;
      color: var(--fl-muted);
    }
  }

  .fl-health {
    margin-top: 22px;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 8px;
    font-size: 11px;
    font-weight: 500;

    i {
      grid-column: 1 / -1;
      height: 5px;
      background: #e5eae4;
    }

    em {
      display: block;
      height: 100%;
      background: var(--fl-green);
    }
  }

  /* --- generic section ------------------------------------------------- */

  .fl-section {
    padding: 105px 7vw;

    h2 {
      font-size: clamp(42px, 5vw, 68px);
      line-height: .95;
      letter-spacing: -.05em;
      margin: 20px 0 0;
    }
  }

  .fl-section-title {
    display: grid;
    grid-template-columns: 1.2fr .6fr;
    gap: 8vw;
    align-items: end;
    margin-bottom: 55px;

    > p {
      line-height: 1.7;
      color: var(--fl-muted);
      font-size: 14px;
      margin: 0;
    }
  }

  /* --- 01 the organisation --------------------------------------------- */

  .fl-profile-intro {
    background: #fff;
  }

  .fl-profile-heading {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 24px;
    align-items: center;

    h2 {
      font-size: clamp(38px, 5vw, 66px);
      letter-spacing: -.045em;
      margin: 8px 0 2px;
      line-height: 1;
    }

    p {
      font-size: 20px;
      margin: 0;
    }
  }

  .fl-logo {
    width: 96px;
    height: 96px;
    border-radius: 8px;
    background: var(--fl-pale) center / contain no-repeat;
    display: grid;
    place-items: center;
    font-size: 31px;
    font-weight: 500;
    position: relative;

    button {
      position: absolute;
      right: -6px;
      bottom: -6px;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      border: 0;
      background: var(--fl-green);
      color: #fff;
      cursor: pointer;
    }
  }

  .fl-verified {
    font-size: 11px;
    color: var(--fl-green);
  }

  .fl-location {
    font-size: 11px;
    color: var(--fl-muted);
    margin-top: 10px;
  }

  .fl-editlink {
    border: 0;
    background: none;
    text-decoration: underline;
    font-family: inherit;
    font-size: 11px;
    color: inherit;
    cursor: pointer;
    align-self: start;
  }

  .fl-facts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    margin: 50px 0 90px;
    border: 1px solid var(--fl-line);

    > div {
      padding: 22px;
      border-left: 1px solid var(--fl-line);
      position: relative;

      &:first-child {
        border-left: 0;
      }

      &.is-primary {
        background: var(--fl-pale);
      }
    }

    span {
      display: block;
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: .1em;
      color: var(--fl-muted);
      margin-bottom: 9px;
    }

    strong {
      font-size: 15px;
    }

    em {
      position: absolute;
      right: 20px;
      bottom: 19px;
      color: var(--fl-green);
      font-style: normal;
      font-weight: 500;
      font-size: 13px;
    }
  }

  .fl-company-grid {
    display: grid;
    grid-template-columns: 1.35fr .65fr;
    gap: 8vw;
  }

  .fl-company-story {
    h3 {
      font-size: clamp(34px, 4vw, 55px);
      line-height: 1;
      letter-spacing: -.04em;
      margin: 25px 0;
    }
  }

  .fl-copy {
    color: var(--fl-muted);
    font-size: 17px;
    line-height: 1.75;
  }

  .fl-readmore button {
    border: 0;
    background: none;
    padding: 0;
    font-family: inherit;
    font-size: 11px;
    text-decoration: underline;
    color: inherit;
    cursor: pointer;
  }

  .fl-glance {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    border-top: 1px solid var(--fl-line);
    margin-top: 38px;
    padding-top: 24px;

    b {
      font-size: 26px;
      display: block;
    }

    span {
      display: block;
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: .1em;
      color: var(--fl-muted);
      margin-top: 6px;
    }
  }

  .fl-products-card {
    background: var(--fl-pale);
    padding: 28px;
    border-radius: 7px;
    align-self: start;

    > a {
      display: grid;
      grid-template-columns: 26px 1fr auto;
      gap: 10px;
      padding: 18px 0;
      border-top: 1px solid #cbd8cb;
      font-size: 12px;
      cursor: pointer;

      > b {
        font-size: 9px;
        color: var(--fl-muted);
      }

      > i {
        font-style: normal;
      }

      &:hover {
        color: var(--fl-green);
      }
    }

    > small {
      display: block;
      margin-top: 14px;
      color: var(--fl-muted);
      font-size: 9px;
    }
  }

  .fl-card-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-transform: uppercase;
    letter-spacing: .1em;
    font-size: 10px;
    margin-bottom: 16px;

    button {
      border: 0;
      background: none;
      font-family: inherit;
      font-size: 10px;
      text-decoration: underline;
      color: inherit;
      cursor: pointer;
    }

    em {
      font-style: normal;
      color: var(--fl-muted);
    }
  }

  .fl-expertise {
    margin-top: 70px;
    padding: 40px;
    background: var(--fl-sand);
    display: grid;
    grid-template-columns: 1fr 1.1fr;
    gap: 7vw;
    align-items: center;

    h3 {
      font-size: 35px;
      line-height: 1;
      letter-spacing: -.04em;
      margin: 25px 0;
    }

    p {
      line-height: 1.6;
      color: var(--fl-muted);
      font-size: 14px;
      margin: 0;
    }
  }

  .fl-expertise__tags {
    display: grid;
    gap: 8px;

    > span {
      background: #fff;
      padding: 15px 18px;
      display: flex;
      justify-content: space-between;
      gap: 16px;
      font-size: 14px;
    }

    small {
      color: var(--fl-muted);
      font-size: 11px;
      white-space: nowrap;
    }
  }

  /* A full-width strip along the bottom of the expertise panel. The panel is a two-column grid,
     so without the span this would drop into the left column beside the tags. */
  .fl-sdgs {
    grid-column: 1 / -1;

    /* Only when it follows the tags — as the panel's sole content it needs no divider. */
    &:not(:nth-child(2)) {
      margin-top: 8px;
      padding-top: 32px;
      border-top: 1px solid rgba(51, 65, 62, .12);
    }
  }

  .fl-sdgs__label {
    display: block;
    margin-bottom: 16px;
    text-transform: uppercase;
    letter-spacing: .12em;
    font-size: 10px;
    font-weight: 500;
    color: var(--fl-muted);
  }

  .fl-sdgs__row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-content: flex-start;

    button {
      display: block;
      width: 56px;
      height: 56px;
      padding: 0;
      border: 0;
      background: none;
      cursor: pointer;
      // The artwork is a full-bleed coloured square, so it carries its own edge; a border would
      // read as a second one.
      transition: transform .2s, opacity .2s;

      img {
        display: block;
        width: 100%;
        height: 100%;
      }

      &:hover {
        transform: translateY(-2px);
        opacity: .85;
      }

      &:focus-visible {
        outline: 2px solid var(--fl-green);
        outline-offset: 2px;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      button {
        transition: none;

        &:hover {
          transform: none;
        }
      }
    }
  }

  /* --- 02 roles -------------------------------------------------------- */

  .fl-roles {
    background: var(--fl-pale);

    article {
      display: grid;
      grid-template-columns: 52px 1fr 25px;
      gap: 15px;
      align-items: center;
      padding: 25px 4px;
      border-top: 1px solid #cdd7ce;

      > b {
        font-size: 10px;
        color: var(--fl-muted);
      }

      h3 {
        margin: 0 0 5px;
        font-size: 19px;
        font-weight: 500;
      }

      > div > span {
        font-size: 9px;
        text-transform: uppercase;
        letter-spacing: .08em;
        color: var(--fl-green);
      }

      > i {
        font-style: normal;
      }
    }
  }

  /* --- 03 transitions -------------------------------------------------- */

  .fl-transitions {
    background: #fff;
  }

  .fl-transition-tabs {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 7px;
    margin-bottom: 9px;

    button {
      border: 1px solid var(--fl-line);
      background: #fff;
      text-align: left;
      padding: 16px;
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 5px;
      font-family: inherit;
      color: inherit;
      cursor: pointer;

      b {
        font-size: 9px;
        color: var(--fl-muted);
        grid-column: 1 / -1;
      }

      span {
        font-weight: 500;
        font-size: 12px;
      }

      em {
        font-style: normal;
        font-weight: 500;
      }

      &[aria-selected='true'] {
        border-color: var(--fl-accent);
        box-shadow: inset 0 -5px var(--fl-accent);
      }
    }
  }

  .fl-transition-detail {
    display: grid;
    grid-template-columns: .65fr 1.45fr .7fr;
    gap: 4vw;
    padding: 45px;
    border: 1px solid var(--fl-line);
    border-radius: 7px;
  }

  // Dark on a white section, and open by default: this is the one block on the page that tells an
  // owner what to actually do, so it is styled to be found rather than to blend in. Still a
  // <details> so it can be collapsed once acted on.
  .fl-score-help {
    margin-top: 48px;
    // Real bottom padding rather than leaning on the last child's margin: with `padding-bottom: 0`
    // and no bottom border, that margin collapses out through the panel edge and the closing line
    // ends up flush against it. Only when open — closed, the summary's own padding is the whole box.
    padding: 0 28px;
    border-radius: 8px;
    background: var(--fl-ink);
    color: #fff;
    box-shadow: 0 24px 60px rgba(51, 65, 62, .22);

    > summary {
      display: flex;
      align-items: baseline;
      flex-wrap: wrap;
      gap: 4px 14px;
      padding: 22px 0;
      cursor: pointer;
      list-style: none;

      // The default triangle sits badly against this layout; the chevron below replaces it.
      &::-webkit-details-marker {
        display: none;
      }

      b {
        font-size: 17px;
        letter-spacing: -.02em;
      }

      span {
        flex: 1;
        font-size: 11px;
        color: rgba(255, 255, 255, .62);
      }

      // Chevron, rotated when open.
      &::after {
        content: '⌄';
        font-size: 15px;
        line-height: 1;
        color: var(--fl-green);
        transition: transform .18s ease;
      }
    }

    &[open] {
      padding-bottom: 28px;

      > summary {
        border-bottom: 1px solid rgba(255, 255, 255, .14);
        margin-bottom: 22px;

        &::after {
          transform: rotate(180deg);
        }
      }
    }

    p {
      font-size: 12.5px;
      line-height: 1.65;
      color: rgba(255, 255, 255, .82);
    }

    &__lead {
      margin: 0 0 22px;

      strong {
        color: var(--fl-green);
      }
    }

    &__list {
      list-style: none;
      margin: 0 0 22px;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 15px;

      li {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 20px;
        padding-bottom: 15px;
        border-bottom: 1px solid rgba(255, 255, 255, .1);

        &:last-child {
          border-bottom: 0;
          padding-bottom: 0;
        }
      }

      b {
        display: block;
        margin-bottom: 5px;
        font-size: 12.5px;
        color: #fff;
      }

      span {
        font-size: 11.5px;
        line-height: 1.6;
        color: rgba(255, 255, 255, .66);
      }

      button {
        flex: none;
        border: 1px solid rgba(255, 255, 255, .28);
        border-radius: 4px;
        padding: 8px 13px;
        background: none;
        font-family: inherit;
        font-size: 10px;
        font-weight: 500;
        color: #fff;
        white-space: nowrap;
        cursor: pointer;
        transition: background .15s ease, border-color .15s ease, color .15s ease;

        &:hover {
          border-color: var(--fl-green);
          background: var(--fl-green);
          color: var(--fl-ink);
        }
      }
    }

    // The one thing an actor most needs to read, so it does not sit in the list as item six.
    &__highlight {
      margin-bottom: 20px;
      padding: 16px 18px;
      border-radius: 5px;
      border-left: 3px solid var(--fl-green);
      background: rgba(255, 255, 255, .07);

      b {
        display: block;
        margin-bottom: 6px;
        font-size: 12.5px;
        color: var(--fl-green);
      }

      p {
        margin: 0;
      }
    }

    // No bottom margin — the panel's own padding-bottom spaces this off the edge.
    &__caveat {
      margin: 0;
      font-size: 11px !important;
      color: rgba(255, 255, 255, .55) !important;
    }
  }

  .fl-score-block {
    text-align: center;

    > span {
      display: block;
      margin: 18px 0;
      font-size: 11px;
      font-weight: 500;
    }

    button {
      border: 0;
      background: none;
      font-family: inherit;
      font-size: 10px;
      text-decoration: underline;
      color: inherit;
      cursor: pointer;
    }
  }

  .fl-score-ring {
    width: 158px;
    height: 158px;
    margin: auto;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: conic-gradient(var(--fl-accent) var(--fl-score), #e8ede7 0);
    position: relative;

    &:before {
      content: '';
      position: absolute;
      inset: 13px;
      background: #fff;
      border-radius: 50%;
    }

    strong {
      position: relative;
      z-index: 1;
      font-size: 48px;
      font-weight: 500;
      line-height: 1;
    }

    small {
      position: absolute;
      z-index: 1;
      bottom: 40px;
      font-size: 11px;
      color: var(--fl-muted);
    }
  }

  .fl-transition-content {
    h3 {
      font-size: 31px;
      line-height: 1.12;
      letter-spacing: -.02em;
      margin: 14px 0 20px;
      font-weight: 500;
    }
  }

  .fl-domain-tags {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;

    span {
      font-size: 9px;
      background: #eef2ec;
      padding: 7px 9px;
      border-radius: 3px;
    }
  }

  .fl-narrative {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin-top: 30px;

    > div {
      padding: 15px;
      border-top: 1px solid var(--fl-line);
      border-left: 1px solid var(--fl-line);

      &:nth-child(odd) {
        border-left: 0;
      }
    }

    span {
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: .08em;
      color: var(--fl-muted);
    }

    p {
      font-size: 11px;
      line-height: 1.5;
      margin: 8px 0 0;
    }
  }

  .fl-metrics {
    > div {
      margin-bottom: 20px;
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 8px;

      > i {
        grid-column: 1 / -1;
        height: 5px;
        background: #e5eae4;
      }

      > i > em {
        display: block;
        height: 100%;
        background: var(--fl-accent);
      }
    }

    span {
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: .08em;
      color: var(--fl-muted);
    }

    b {
      font-weight: 500;
    }

    small {
      color: var(--fl-muted);
      font-size: 9px;
    }
  }

  /* --- 04 technology --------------------------------------------------- */

  .fl-technology {
    background: var(--fl-mint);
  }

  .fl-actor-path {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #fff;
    padding: 12px 18px;
    margin-bottom: 12px;
    font-size: 10px;
    flex-wrap: wrap;

    > span {
      background: var(--fl-green);
      color: #fff;
      padding: 7px 10px;
    }

    > i {
      font-style: normal;
      color: var(--fl-muted);
    }

    button {
      margin-left: auto;
      border: 0;
      background: none;
      font-family: inherit;
      font-size: 10px;
      text-decoration: underline;
      color: inherit;
      cursor: pointer;
    }
  }

  .fl-tech-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .fl-tech-card {
    padding: 27px;
    background: #fff;
    border-radius: 6px;

    &--main {
      border-top: 6px solid var(--fl-green);
    }

    &--muted {
      opacity: .78;
      border-top: 6px solid var(--fl-blue);
    }
  }

  .fl-tech-row {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    gap: 15px;
    padding: 19px 0;
    border-top: 1px solid var(--fl-line);
    font-size: 11px;

    > b {
      font-size: 14px;
      font-weight: 500;
    }

    > span {
      color: var(--fl-muted);
    }

    > em {
      font-style: normal;
      text-transform: uppercase;
      color: var(--fl-green);
      font-size: 9px;
    }
  }

  /* --- 05 needs, offers & matches -------------------------------------- */

  .fl-opportunities {
    background: #fff;
  }

  .fl-needs-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .fl-need-card {
    padding: 28px;
    border-radius: 6px;

    &--needs {
      background: #f7eadf;
    }

    &--offers {
      background: #edf3e4;
    }

    > span {
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: .1em;
    }

    h3 {
      margin: 20px 0;
      font-size: 18px;
      font-weight: 500;
    }

    > p {
      margin: 20px 0;
      font-size: 13px;
      color: var(--fl-muted);
    }

    button {
      border: 0;
      background: none;
      font-family: inherit;
      font-size: 10px;
      text-decoration: underline;
      color: inherit;
      cursor: pointer;
    }
  }

  .fl-match-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 12px;
    margin-top: 45px;

    article {
      border: 1px solid var(--fl-line);
      padding: 25px;
      min-height: 225px;
      display: flex;
      flex-direction: column;

      > span {
        color: var(--fl-green);
        font-size: 10px;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: .1em;
      }

      h3 {
        font-size: 22px;
        font-weight: 500;
        letter-spacing: -.02em;
        margin: 14px 0 8px;
      }

      p {
        color: var(--fl-muted);
        font-size: 11px;
        line-height: 1.5;
        margin: 0;
      }

      a {
        margin-top: auto;
        border-top: 1px solid var(--fl-line);
        padding-top: 14px;
        font-size: 11px;
        cursor: pointer;

        &:hover {
          color: var(--fl-green);
        }
      }
    }
  }

  /* --- contacts -------------------------------------------------------- */

  .fl-contacts {
    background: var(--fl-pale);
  }

  /* --- closing --------------------------------------------------------- */

  .fl-closing {
    text-align: center;
    padding: 110px 7vw;
    background: var(--fl-mint);

    h2 {
      font-size: clamp(38px, 5vw, 65px);
      line-height: 1;
      letter-spacing: -.04em;
      max-width: 920px;
      margin: 0 auto 28px;
    }

    // An actor name is arbitrary length, unlike the design's hand-set headline.
    h2.fl-closing__title--actor {
      font-size: clamp(32px, 4.2vw, 55px);
      overflow-wrap: anywhere;
    }

    p {
      color: var(--fl-muted);
      max-width: 560px;
      margin: 0 auto 30px;
      line-height: 1.6;
    }
  }

  .fl-closing__actions {
    display: flex;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  /* --- more profiles --------------------------------------------------- */

  .fl-more {
    padding: 80px 7vw;
    background: #fff;

    .fl-section-no {
      margin-bottom: 24px;
    }
  }

  .fl-more__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 20px;
  }

  /* --- responsive ------------------------------------------------------ */

  @media (max-width: 900px) {
    .fl-topbar__nav {
      display: none;
    }

    .fl-activation,
    .fl-company-grid,
    .fl-expertise,
    .fl-section-title,
    .fl-transition-detail {
      grid-template-columns: 1fr;
    }

    .fl-transition-detail {
      gap: 35px;
    }

    .fl-tech-grid,
    .fl-needs-grid {
      grid-template-columns: 1fr;
    }

    .fl-profile-heading {
      grid-template-columns: auto 1fr;
    }

    .fl-editlink {
      grid-column: 2;
    }

  }

  @media (max-width: 560px) {
    .fl-topbar {
      padding: 12px 20px;
    }

    .fl-activation,
    .fl-section,
    .fl-closing,
    .fl-more {
      padding-left: 22px;
      padding-right: 22px;
    }

    .fl-activation {
      padding-top: 60px;
    }

    .fl-activation__actions {
      align-items: flex-start;
      flex-direction: column;
    }

    .fl-profile-heading {
      grid-template-columns: 1fr;
    }

    .fl-editlink {
      grid-column: 1;
      justify-self: start;
    }

    .fl-facts {
      grid-template-columns: 1fr;

      > div {
        border-left: 0;
        border-top: 1px solid var(--fl-line);
      }
    }

    .fl-logo {
      width: 72px;
      height: 72px;
      font-size: 24px;
    }

    .fl-transition-tabs,
    .fl-narrative {
      grid-template-columns: 1fr;
    }

    .fl-narrative > div {
      border-left: 0;
    }

    .fl-tech-row {
      grid-template-columns: 1fr auto;

      > span {
        grid-column: 1 / -1;
      }
    }
  }

  @media print {
    .fl-topbar,
    .fl-activation__actions,
    .fl-closing,
    .fl-more {
      display: none;
    }

    .fl-activation {
      min-height: auto;
    }

    .fl-section {
      padding: 50px 6vw;
    }

    .fl-match-row article {
      break-inside: avoid;
    }
  }
</style>
